/**
 * English dictionary for the Nxtpath console. It
 * keeps the forked console's page-level key space; `zh-CN.ts` mirrors it
 * key for key, in the same order (`test/i18n.test.tsx` enforces both).
 *
 * Value type is widened to `string`; the key-space guarantee is the contract
 * we need, while the value is free-form by design.
 *
 * Copy style
 * - Sentence-case for user-facing text (not Title Case), matching the
 *   existing console-web conventions in the pages before this PR.
 * - Use en-dash (`–`) for numeric ranges (e.g. `10–128 chars`) — same
 *   punctuation the pre-i18n sources used.
 * - Prefer imperative verbs in buttons ("Sign in", not "Please sign in").
 */
export const en = {
  // ── <head> metadata ─────────────────────────────────────────────
  'meta.title': 'Nxtpath Console',
  'meta.description':
    'Nxtpath developer console: manage API keys, review usage, top up balance, and invite teammates.',

  // ── Common / shared ──────────────────────────────────────────────
  'common.brand.title': 'Nxtpath Console',
  'common.session.checking': 'Checking session…',
  'common.back_to_login': 'Back to sign-in',
  'common.technical_details.title': 'Technical details',
  'common.technical_details.show': 'Show',
  'common.technical_details.hide': 'Hide',
  // The one placeholder for a key that has neither a name nor a prefix
  // left to identify it. Shared by /keys, /requests, /usage,
  // /dashboard, /optimization and the member-keys dialog through
  // `components/apiKeyName.ts`, which is why it is not page-scoped:
  // three page-scoped variants of it are exactly what #47 was.
  'api_key.unnamed': 'Unnamed key',
  // Finance Excel export (#178). Cap copy is an instruction, not a
  // request — the Chinese catalog must not say 「请」(DESIGN.md §7).
  'export.idle': 'Export Excel',
  'export.working': 'Exporting…',
  'export.done': 'Excel exported',
  'export.error': 'Excel export failed.',
  'export.cap': 'Range too large. Narrow the time window and export again.',

  // ── Login ───────────────────────────────────────────────────────
  'login.eyebrow': 'Sign in',
  'login.subtitle': 'Sign in to your Nxtpath account',
  'login.description': 'Manage API keys, balance, usage, and teammates after you sign in.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.need_account': 'Need an account? Register',
  'login.have_invite_code': 'Have an invite code? Activate',
  'login.forgot': 'Forgot password?',
  'login.error.empty_fields': 'Email and password are required',

  // ── Login · email sign-in code (epic #1475) ─────────────────────
  // The conditional "If … has an account" phrasing is deliberate: the
  // request endpoint answers identically for registered and unknown
  // addresses, and the copy must not give away what the API withholds.
  'login.mode.label': 'Sign-in method',
  'login.mode.password': 'Password',
  'login.mode.code': 'Email code',
  'login.code.send': 'Send code',
  'login.code.sending': 'Sending…',
  'login.code.label': '6-digit code',
  'login.code.ack':
    'If {email} has an account, a code is on its way. It expires in {minutes} minutes.',
  'login.code.ack_resent':
    'Sent again. If {email} has an account, the new code expires in {minutes} minutes.',
  'login.code.resend': 'Resend',
  'login.code.change_email': 'Use another email',
  'login.code.error.empty_email': 'Email is required',
  'login.code.error.format': 'The code is 6 digits',
  'login.code.error.invalid': 'That code is not right — check it and try again',
  'login.code.error.expired': 'That code has expired — request a new one',
  'login.code.error.attempts_exhausted': 'Too many attempts — request a new code',
  'login.code.error.send_rate_limited': 'Too many requests — try again in a moment',
  'login.code.error.verify_rate_limited': 'Too many attempts — try again in a moment',
  'login.code.error.account_suspended': 'This account is suspended. Contact your account manager.',
  'login.code.error.account_deleted': 'This account has been closed',

  // ── Activate (invite-code activation, epic #1475) ───────────────
  'activate.subtitle': 'Activate with an invite code',
  'activate.description':
    'Enter your email and the invite code from your account manager to open an account and claim your trial credit.',
  'activate.email': 'Email',
  'activate.invite_code': 'Invite code',
  'activate.submit': 'Activate account',
  'activate.submitting': 'Activating…',
  'activate.have_account': 'Already have an account? Sign in',
  'activate.no_code': 'No invite code? Register',
  'activate.error.empty_fields': 'Email and invite code are required',
  'activate.error.code_invalid':
    'That invite code is not valid or is no longer active — check with your account manager',
  'activate.error.quota_exhausted':
    'This invite code is out of slots for today — try tomorrow or ask your account manager',
  'activate.error.email_taken': 'That email already has an account',
  'activate.error.email_taken_cta': 'Sign in instead',
  'activate.error.rate_limited': 'Too many attempts — try again in a moment',
  'activate.success.title': 'Your account is ready',
  'activate.success.description':
    'Your trial credit is in the account — you can start calling the API now.',
  'activate.success.grant_title': 'Activated',
  'activate.success.grant_body': '{amount} trial credit added, valid until {expiresAt}.',
  'activate.success.passwordless_hint':
    'This account has no password — sign in with an email code next time.',
  'activate.success.cta': 'Go to console',

  // ── Register ────────────────────────────────────────────────────
  'register.eyebrow': 'Create account',
  'register.subtitle': 'Create your Nxtpath account',
  'register.description': 'Start with your own account, then invite teammates when you are ready.',
  'register.email': 'Email',
  'register.password_with_hint': 'Password (10–128 chars)',
  'register.workspace_name': 'Team name (optional)',
  'register.workspace_placeholder': 'For example: Payments / Data Platform / My Team',
  'register.submit': 'Create account',
  'register.submitting': 'Creating account…',
  'register.error.empty_fields': 'Email and password are required',
  'register.error.password_too_short': 'Password must be at least 10 characters',
  'register.error.email_taken':
    "That email already has an account. If you haven't verified it yet, check your inbox for the verification email or resend the link from the sign-in page.",
  'register.error.email_taken_cta': 'Go to sign in',
  'register.error.invalid_email': 'Enter a valid email address',
  'register.error.password_length': 'Password must be 10–128 characters',
  'register.success.title': 'Check your inbox',
  'register.success.body':
    'We sent a verification link to {email}. Open the link in the email to continue into the console.',
  'register.success.resend_ack':
    'A new verification link is on its way. If it still does not arrive, check spam or wait a minute before trying again.',
  'register.success.resend_hint': 'If the email has not arrived yet, you can send another one.',
  'register.resend.submit': 'Resend link',
  'register.resend.submitting': 'Sending…',
  'register.already_have_account': 'Already have an account? Sign in',

  // ── Forgot password ─────────────────────────────────────────────
  'forgot.eyebrow': 'Recover access',
  'forgot.subtitle': 'Reset your sign-in password',
  'forgot.description': 'Enter your account email and we will send you a reset link.',
  'forgot.email': 'Email',
  'forgot.submit': 'Send reset link',
  'forgot.submitting': 'Sending…',
  'forgot.error.empty_email': 'Email is required',
  'forgot.success.title': 'Check your email',
  'forgot.success.body':
    'If an account exists for {email}, we sent a reset link. The link is valid for 24 hours.',

  // ── Reset password ──────────────────────────────────────────────
  'reset.eyebrow': 'New password',
  'reset.subtitle': 'Set a new password',
  'reset.description': 'Save the new password, then head back to sign in.',
  'reset.new_password': 'New password (10–128 chars)',
  'reset.confirm_password': 'Confirm new password',
  'reset.submit': 'Save password',
  'reset.submitting': 'Saving…',
  'reset.error.missing_token': 'Reset token missing from link',
  'reset.error.password_too_short': 'Password must be at least 10 characters',
  'reset.error.password_mismatch': 'Passwords do not match',
  'reset.error.token_banner':
    'Reset token missing — open the email link directly, or request a new one.',
  'reset.success.title': 'Password reset',
  'reset.success.body': 'Your password has been updated. Sign in with the new password.',
  'reset.success.sign_in': 'Sign in',

  // ── Verify email ────────────────────────────────────────────────
  'verify.subtitle': 'Finish verifying your email',
  'verify.description': 'Once verification finishes, use the button below to enter the console.',
  'verify.success.title': 'Email verified',
  'verify.success.body': 'Your email address is verified and your account is ready to use.',
  'verify.success.cta': 'Enter the console',
  'verify.missing_token':
    'This link is missing the verification token. Open the email link directly, or request a fresh one from the sign-in page.',
  'verify.pending': 'Verifying your email…',
  'verify.error.title': 'Verification failed',
  'verify.resend.label': 'Request a new link',
  'verify.resend.submit': 'Send a new link',
  'verify.resend.submitting': 'Sending…',
  'verify.resend.ack.title': 'Check your inbox',
  'verify.resend.ack.body':
    'If an account exists for that address and has not yet been verified, a fresh link is on its way.',

  // ── Invitation ──────────────────────────────────────────────────
  'invite.eyebrow': 'Team invitation',
  'invite.subtitle': 'Join a team',
  'invite.description': 'Review the team details first, then finish joining.',
  'invite.loading': 'Loading invitation…',
  'invite.error.missing_token': 'Missing invitation token in the URL',
  'invite.error.title': 'Invitation unavailable',
  'invite.invite_to': "You're invited to",
  'invite.role': 'Role',
  'invite.invitee_email': 'Invitee email',
  'invite.invited_by': 'Invited by',
  'invite.expires': 'Expires',
  'invite.password_with_hint': 'Password (10–128 chars)',
  'invite.display_name': 'Display name (optional)',
  'invite.display_name_placeholder': 'Defaults to email prefix',
  'invite.session_match': "You're signed in as {email}. Click accept to join {org}.",
  'invite.session_mismatch.body':
    "You're currently signed in as {current}, but this invitation is for {target}. Enter the password for that account to accept, or ",
  'invite.session_mismatch.switch_link': 'switch account',
  'invite.session_mismatch.suffix': '.',
  'invite.accept': 'Accept invitation',
  'invite.join': 'Join team',
  'invite.accepting': 'Accepting…',
  'invite.cancel': 'Cancel',
  'invite.error.password_too_short': 'Password must be at least {min} characters',
  'invite.error.password_too_long': 'Password must be at most {max} characters',
  'invite.toast.accepted': 'Invitation accepted',

  // ── Not found ──────────────────────────────────────────────────
  'notfound.title': 'Page not found',
  'notfound.body': 'The page you were looking for does not exist in this console.',
  'notfound.cta': 'Go to dashboard',

  // ── Shell chrome ───────────────────────────────────────────────
  'shell.brand.subtitle': 'API Console',
  'shell.brand.supporting': 'Manage your API keys, balance, usage, and teammates in one place.',
  'shell.nav.aria_label': 'Primary',
  'shell.nav.dashboard': 'Overview',
  'shell.nav.keys': 'Keys',
  'shell.nav.usage': 'Usage',
  'shell.nav.organization': 'Organization',
  'shell.nav.requests': 'Request logs',
  'shell.nav.optimization': 'Optimization',
  'shell.nav.pricing': 'Model pricing',
  'shell.nav.billing': 'Balance & billing',
  'shell.nav.settings': 'Account & team',
  'shell.nav.docs': 'Integration docs',
  'shell.nav.status': 'Service status',
  'shell.nav.tickets': 'Support tickets',
  'shell.nav.open': 'Open navigation',
  'shell.nav.close': 'Close navigation',
  'shell.sidebar.collapse': 'Collapse sidebar',
  'shell.sidebar.expand': 'Expand sidebar',
  'shell.cmdk.trigger': 'Search or jump to…',
  'shell.cmdk.placeholder': 'Search pages, or paste a request id',
  'shell.cmdk.empty': 'No matches',
  'shell.cmdk.aria_label': 'Command palette',
  'shell.cmdk.footer_hint': '↑↓ move · ↵ open · esc close',
  'shell.cmdk.group.recent': 'Recent',
  'shell.cmdk.group.navigation': 'Navigation',
  'shell.cmdk.group.requests': 'Request logs',
  'shell.cmdk.open_request': 'Open request {id}',
  'shell.cmdk.hint.req': 'req',
  'shell.bell.aria': 'Notifications',
  'shell.bell.aria_unread': 'Notifications ({count} unread)',
  'shell.header.tenant_chip': 'Nxtpath tenant',
  'shell.org_chip.title': 'Current team',
  'shell.page.console_fallback': 'Console',
  'shell.session.active': 'Current session',
  'shell.session.verified': 'Email verified',
  'shell.session.unverified': 'Email verification pending',
  'shell.session.signed_in': 'signed in',
  'shell.logout': 'Log out',

  // ── Dashboard ──────────────────────────────────────────────────
  'dashboard.title': 'Overview',
  'dashboard.subtitle': 'Quickly check your balance, usage, and recent changes.',
  'dashboard.subtitle_no_billing': 'Quickly check your usage and recent changes.',
  'dashboard.stat.balance.label': 'Available balance',
  'dashboard.stat.balance.updated': 'Updated {timestamp}',
  'dashboard.stat.balance.unavailable': 'Balance unavailable',
  'dashboard.stat.balance.loading': 'Loading…',
  'dashboard.stat.balance.zero_cta': 'Your balance is empty — top up now',
  'dashboard.stat.balance.overdrawn': 'Balance is overdrawn',
  // Says only that the value is out of reach. It must never name an
  // amount or a direction: the query behind this card is not sent to
  // the reader who sees this line.
  'dashboard.stat.balance.no_access': 'Balance is visible to owners and admins only.',
  'dashboard.stat.cost.label': 'This month',
  'dashboard.stat.cost.tokens': '{tokens} tokens you used this month',
  'dashboard.stat.cost.tokens_org': '{tokens} tokens used across the organization this month',
  'dashboard.stat.cost.unavailable': 'Usage unavailable',
  'dashboard.stat.cost.loading': 'Loading…',
  'dashboard.stat.keys.label': 'Available keys',
  'dashboard.stat.keys.description': 'Keys that can still be used right now',
  'dashboard.stat.keys.description_org_cta':
    'Every key in the organization — view the member directory',
  'dashboard.stat.keys.unavailable': 'Keys unavailable',
  'dashboard.stat.keys.empty_cta': 'No usable keys yet — create your first one',
  // Same restraint as `dashboard.stat.balance.no_access`: the query is
  // not sent, so this line must not name a count or invite the reader
  // to a /keys page they cannot open.
  'dashboard.stat.keys.no_access': 'Keys are visible to owners, admins, and members only.',
  'dashboard.stat.last_request.label': 'Last request',
  'dashboard.stat.last_request.empty': 'No requests yet',
  'dashboard.stat.last_request.hint': 'Updates automatically when new requests arrive',
  'dashboard.stat.last_request.empty_cta': 'Create a key to start sending requests',
  'dashboard.status.caption': 'Service status',
  'dashboard.status.overall.healthy': 'All services operational',
  'dashboard.status.overall.busy': 'Some services busy',
  'dashboard.status.overall.unavailable': 'Some services unavailable',
  'dashboard.status.updated': 'Updated {timestamp}',
  'dashboard.status.view': 'View service status',
  'dashboard.status.error': 'Could not load service status.',
  'dashboard.status.empty': 'No service data yet',
  'dashboard.quick.caption': 'Quick actions',
  'dashboard.quick.create_key': 'Create key',
  'dashboard.quick.topup': 'Top up',
  'dashboard.quick.requests': 'Request diagnostics',
  'dashboard.quick.pricing': 'Model pricing',
  'dashboard.trend.requests.caption': 'Requests — last 7 days',
  'dashboard.trend.spend.caption': 'Spend — last 7 days',
  'dashboard.trend.view_usage': 'View usage analytics',
  'dashboard.trend.error': 'Could not load trend data.',
  'dashboard.trend.empty': 'No data yet',
  'dashboard.trend.incomplete': 'partial day',
  'dashboard.trend.incomplete_note': 'Today is still being recorded, so the last point is partial.',
  'dashboard.activity.title': 'Recent requests',
  'dashboard.activity.view_all': 'View all usage',
  'dashboard.activity.loading': 'Loading recent activity…',
  'dashboard.activity.error': 'Could not load recent activity.',
  'dashboard.retry': 'Retry',
  'dashboard.activity.empty': 'No requests in the last 7 days yet.',
  'dashboard.activity.empty_cta': 'Create a key, then send your first request',
  'dashboard.activity.column.hour': 'Hour',
  'dashboard.activity.column.requests': 'Requests',
  'dashboard.activity.column.usage': 'Usage',
  'dashboard.activity.column.cost': 'Cost',
  'dashboard.activity.unit.tokens': 'tokens',
  'dashboard.top_keys.title': 'Most-used keys this month',
  'dashboard.top_keys.manage': 'Manage keys',
  'dashboard.top_keys.loading': 'Loading key usage…',
  'dashboard.top_keys.error': 'Could not load key usage.',
  'dashboard.top_keys.empty.chip': 'no usage yet',
  'dashboard.top_keys.empty.hint': 'Keys will show up here once they start handling requests.',
  'dashboard.top_keys.empty_cta': 'Create a key',
  'dashboard.top_keys.column.key': 'Key',
  'dashboard.top_keys.column.requests': 'Requests',
  'dashboard.top_keys.column.cost': 'Cost',

  // ── API keys page ──────────────────────────────────────────────
  'keys.title': 'API keys',
  'keys.subtitle': 'Create and manage the keys your applications use to call Nxtpath.',
  'keys.create': 'Create key',
  'keys.base_url.title': 'Connection URL',
  'keys.base_url.description': 'Use this base URL in your SDK, client, or server configuration.',
  'keys.loading': 'Loading keys…',
  'keys.error': 'Could not load keys',
  'keys.empty': 'No keys yet — create one to start making requests.',
  'keys.copied': 'Copied',
  'keys.click_to_copy': 'Click to copy',
  'keys.show_revoked': 'Show revoked ({count})',
  'keys.column.prefix': 'Prefix',
  'keys.column.name': 'Name',
  'keys.column.spend_limits': 'Spend limits',
  'keys.column.ip_allowlist': 'IP allowlist',
  'keys.column.created': 'Created',
  'keys.column.last_used': 'Last used',
  'keys.column.actions': 'Actions',
  'keys.status.revoked': 'Disabled',
  'keys.status.active': 'Usable',
  'keys.never_used': 'Never',
  'keys.save': 'Save',
  'keys.cancel': 'Cancel',
  'keys.rename': 'Edit',
  'keys.revoke': 'Delete',
  'keys.row_error.name_required': 'Name cannot be empty',
  'keys.row_error.network': 'Network error',
  'keys.limit.none': 'No limit',
  'keys.limit.daily.label': 'Daily',
  'keys.limit.weekly.label': 'Weekly',
  'keys.limit.monthly.label': 'Monthly',
  'keys.limit.daily.placeholder': '¥ / day',
  'keys.limit.weekly.placeholder': '¥ / week',
  'keys.limit.monthly.placeholder': '¥ / month',
  'keys.limit_loading': 'Loading exchange rate…',
  'keys.limit_error.invalid': 'Spend limits must be non-negative RMB amounts.',
  'keys.limit_error.zero':
    'A limit of 0 is not allowed. Leave the field empty for no limit, or revoke the key to stop its spending.',
  'keys.limit_error.rate_unavailable': 'Display exchange rate is unavailable. Try again later.',
  'keys.allowlist.none': 'Unrestricted',
  'keys.allowlist.count': '{count} entries',
  'keys.allowlist.placeholder': '203.0.113.7\n2001:db8::/32',
  'keys.allowlist.hint':
    'One IPv4/IPv6 address or CIDR per line. With a list set, only requests from matching IPs can use this key. Leave empty to allow any IP.',
  'keys.allowlist_error.invalid_entry': 'Line {line}: "{entry}" is not a valid IP address or CIDR.',
  'keys.allowlist_error.too_many': 'The IP allowlist can contain at most {max} entries.',
  'keys.create_modal.title': 'Create API key',
  'keys.create_modal.description':
    'Give this key a name you can recognize later. The full key is only shown once after creation, so save it right away. Model mappings can be adjusted any time afterwards.',
  'keys.create_modal.name_label': 'Key name (optional)',
  'keys.create_modal.name_placeholder': 'production-backend',
  'keys.create_modal.name_default': 'Key {date}',
  'keys.create_modal.limits_label': 'Spend limits in RMB (optional)',
  'keys.create_modal.limits_hint':
    'Leave a field empty for no limit. Each cap is measured against metered usage. The request that crosses a cap still completes — the key is paused once that usage is metered, and comes back when the period rolls over or the cap is raised.',
  'keys.create_modal.allowlist_label': 'IP allowlist (optional)',
  'keys.create_modal.template_label': 'Start from a namespace (optional)',
  'keys.create_modal.template_none': 'No preset mappings',
  'keys.create_modal.template_hint':
    'After creation, the server atomically maps the current {count} models in this namespace (alias = model name).',
  'keys.create_modal.catalog_error':
    'The model catalog could not be loaded. You can still create the key without preset mappings.',
  'keys.create_modal.submit': 'Create key',
  'keys.create_modal.submitting': 'Creating…',
  'keys.create_modal.cancel': 'Cancel',
  'keys.template.failure':
    'The key was created, but its namespace mappings were not applied: {error}',
  'keys.mappings.button': 'Mappings',
  'keys.mappings.eyebrow': 'Keys · model mappings',
  'keys.mappings.title': 'Model mappings',
  'keys.mappings.subtitle': 'Map the model names your client sends to catalog listings. Key: {key}',
  'keys.mappings.close': 'Close model mappings',
  'keys.mappings.loading': 'Loading mappings…',
  'keys.mappings.error': 'Could not load model mappings',
  'keys.mappings.retry': 'Retry',
  'keys.mappings.empty':
    'No mappings yet. Add one so clients can call the target model by that alias.',
  'keys.mappings.column.alias': 'Alias',
  'keys.mappings.column.target': 'Target model',
  'keys.mappings.column.source': 'Source',
  'keys.mappings.source.manual': 'Manual',
  'keys.mappings.source.automatic': 'Automatic',
  'keys.mappings.automatic_managed': 'Managed by namespace',
  'keys.mappings.readonly':
    'You can inspect mappings and conflicts, but your role cannot change them.',
  'keys.mappings.namespace.title': 'Namespace actions',
  'keys.mappings.namespace.description':
    'Choose one namespace. Apply maps its current models once; automatic mapping also includes models added later. Both remove the namespace/ prefix from aliases.',
  'keys.mappings.namespace.select_label': 'Namespace',
  'keys.mappings.namespace.select_placeholder': 'Choose a namespace',
  'keys.mappings.namespace.selected_hint':
    '{count} models are currently visible in this namespace.',
  'keys.mappings.namespace.apply': 'Map current models',
  'keys.mappings.namespace.auto_enable': 'Enable automatic mapping',
  'keys.mappings.namespace.auto_enabled_button': 'Automatic mapping enabled',
  'keys.mappings.namespace.operations_disabled':
    'Mapping current models and enabling new automatic namespaces are temporarily unavailable while the mapping upgrade finishes. Manual mappings and disabling existing automatic namespaces remain available.',
  'keys.mappings.namespace.enabled_title': 'Automatic namespaces',
  'keys.mappings.namespace.enabled_empty': 'No namespaces use automatic mapping.',
  'keys.mappings.namespace.auto_disable': 'Disable',
  'keys.mappings.namespace.applied': 'Mapped {count} current models from {namespace}.',
  'keys.mappings.namespace.auto_enabled': 'Automatic mapping enabled for {namespace}.',
  'keys.mappings.namespace.auto_disabled': 'Automatic mapping disabled for {namespace}.',
  'keys.mappings.namespace.disable_title': 'Disable automatic mapping?',
  'keys.mappings.namespace.disable_description':
    'Automatic mapping from {namespace} will stop. Aliases that also have a manual mapping remain available.',
  'keys.mappings.namespace.disable_confirm': 'Disable automatic mapping',
  'keys.mappings.namespace.disable_cancel': 'Keep enabled',
  'keys.mappings.conflicts.title': 'Conflicting short model names',
  'keys.mappings.conflicts.description':
    'These aliases point to more than one model and are excluded from routing. Disable one automatic namespace or remove a conflicting manual mapping; full namespace/model names still work.',
  'keys.mappings.conflicts.delete_manual': 'Delete manual mapping',
  'keys.mappings.conflicts.delete_title': 'Delete the manual mapping?',
  'keys.mappings.conflicts.delete_description':
    'The manual mapping for {alias} will be deleted. If the remaining automatic candidates are unambiguous, the alias will resolve again.',
  'keys.mappings.conflicts.delete_confirm': 'Delete manual mapping',
  'keys.mappings.conflicts.delete_cancel': 'Keep mapping',
  'keys.mappings.edit': 'Edit',
  'keys.mappings.delete': 'Delete',
  'keys.mappings.add_title': 'Add mapping',
  'keys.mappings.edit_title': 'Change target for {alias}',
  'keys.mappings.alias_label': 'Alias',
  'keys.mappings.alias_placeholder': 'claude-sonnet-4-5',
  'keys.mappings.alias_hint':
    'The name your client sends in the model field. Must not contain "/".',
  'keys.mappings.alias_required': 'Enter an alias',
  'keys.mappings.alias_no_slash': 'Aliases cannot contain "/"',
  'keys.mappings.sku_label': 'Target model',
  'keys.mappings.sku_placeholder': 'Choose a model',
  'keys.mappings.sku_required': 'Choose a model',
  'keys.mappings.submit_add': 'Add mapping',
  'keys.mappings.submit_save': 'Save change',
  'keys.mappings.cancel_edit': 'Cancel edit',
  'keys.mappings.toast.added': 'Mapping added',
  'keys.mappings.toast.updated': 'Mapping updated',
  'keys.mappings.toast.deleted': 'Mapping deleted',
  'keys.mappings.catalog_error':
    'Could not load the model catalog — adding mappings is unavailable.',
  'keys.plaintext.title': 'Copy your API key',
  'keys.plaintext.warning':
    'This is the only time you can see the full key. Copy it now and store it safely; if you lose it, you will need to delete it and create a new one.',
  'keys.plaintext.hint.click_to_copy': 'Click to copy',
  'keys.plaintext.hint.click_with_name': '{name} · click to copy',
  'keys.plaintext.copied': 'Copied to clipboard',
  'keys.plaintext.copy_anywhere': 'Click the key block to copy too',
  'keys.plaintext.copy_button': 'Copy key',
  'keys.plaintext.done': 'Copied, close',
  'keys.plaintext.close': 'I saved it, close',
  'keys.plaintext.copy_aria_label': 'Copy API key to clipboard',
  'keys.revoke_modal.title': 'Delete this API key?',
  'keys.revoke_modal.description.before': 'Deleting the key ',
  'keys.revoke_modal.description.after':
    ' immediately invalidates it. Any application still using it will start receiving 401 errors. This cannot be undone.',
  'keys.revoke_modal.submit': 'Delete',
  'keys.revoke_modal.submitting': 'Deleting…',
  'keys.revoke_modal.cancel': 'Cancel',
  'keys.revoke_modal.close': 'Close',
  'keys.revoke_modal.type_label': 'Type the key name to confirm deletion',

  // ── Service status page (/status) ──────────────────────────────
  'status.title': 'Service status',
  'status.subtitle': 'Separate local setup issues from platform service availability.',
  'status.error': 'Could not load service status.',
  'status.retry': 'Retry',
  'status.kpi.overall': 'Overall state',
  'status.kpi.overall_hint': '{healthy}/{total} services healthy',
  'status.kpi.health': 'Health check',
  'status.kpi.health_hint': 'Platform API probe',
  'status.health.ok': 'OK',
  'status.health.down': 'Down',
  'status.kpi.updated': 'Updated',
  'status.kpi.updated_hint': 'Last refresh of status data',
  'status.state.healthy': 'Healthy',
  'status.state.busy': 'Busy',
  'status.state.unavailable': 'Unavailable',
  'status.timeline.caption': 'Service availability',
  'status.timeline.window': 'Last {hours} hours',
  'status.timeline.uptime': 'uptime',
  'status.timeline.window_start': '{hours} hours ago',
  'status.timeline.window_now': 'now',
  'status.timeline.block_title': '{start}–{end} · {rate}% success',
  'status.timeline.block_no_data': '{start}–{end} · no requests',
  'status.timeline.skus_show': 'Show {count} model details',
  'status.timeline.skus_hide': 'Hide model details',
  'status.events.caption': 'Recent events',
  'status.events.empty': 'No state transitions yet.',
  'status.empty.title': 'No availability data yet',
  'status.empty.hint': 'No service timelines are published yet. Check back later.',
  'status.capacity.bucket_suffix': 'at capacity {ratio}%',
  'status.capacity.legend':
    'Codex rows are colored by at-capacity ratio: <15% green, 15–30% yellow, ≥30% red.',
  'status.official.caption': 'Official provider status',
  'status.official.hint': 'Data from the OpenAI and Anthropic status pages, synced every minute.',
  'status.official.checked_at': 'Checked at {time}',
  'status.official.view_page': 'View official status page',
  'status.official.stale': 'Official status is temporarily unavailable and is shown as unknown.',
  'status.official.no_incidents': 'No official incidents in the past 24 hours.',
  'status.official.all_operational': 'All components operational.',
  'status.official.components_show': 'Show all {count} components',
  'status.official.components_hide': 'Hide all {count} components',
  'status.official.resolved': 'Resolved',
  'status.official.started_at': 'Started {time}',
  'status.official.scheduled': 'Scheduled {start}–{end}',
  'status.official.latest_update': 'Latest update {time}',
  'status.official.expand': 'Show all',
  'status.official.collapse': 'Show less',
  'status.official.indicator.none': 'Operational',
  'status.official.indicator.minor': 'Minor outage',
  'status.official.indicator.major': 'Major outage',
  'status.official.indicator.critical': 'Critical outage',
  'status.official.indicator.maintenance': 'Under maintenance',
  'status.official.indicator.unknown': 'Unknown',
  'status.official.component.operational': 'Operational',
  'status.official.component.degraded_performance': 'Degraded performance',
  'status.official.component.partial_outage': 'Partial outage',
  'status.official.component.major_outage': 'Major outage',
  'status.official.component.under_maintenance': 'Under maintenance',
  'status.official.incident.investigating': 'Investigating',
  'status.official.incident.identified': 'Identified',
  'status.official.incident.monitoring': 'Monitoring',
  'status.official.incident.resolved': 'Resolved',
  'status.official.incident.postmortem': 'Postmortem',
  'status.official.maintenance.scheduled': 'Scheduled',
  'status.official.maintenance.in_progress': 'In progress',
  'status.official.maintenance.verifying': 'Verifying',
  'status.official.maintenance.completed': 'Completed',
  'status.official.impact.none': 'No impact',
  'status.official.impact.minor': 'Minor',
  'status.official.impact.major': 'Major',
  'status.official.impact.critical': 'Critical',

  // ── Integration docs page (/docs) ──────────────────────────────
  'docs.title': 'Integration docs',
  'docs.subtitle':
    'From creating a key and understanding model names and mappings to wiring up Claude Code, Codex, and graphical clients.',
  'docs.toast.copied': 'Copied',
  'docs.copy': 'Copy',
  'docs.toc.aria': 'Integration docs contents',
  'docs.fact.anthropic.title': 'Anthropic Base URL',
  'docs.fact.anthropic.note': 'no /v1',
  'docs.fact.openai.title': 'OpenAI Base URL',
  'docs.fact.openai.note': 'must include /v1',
  'docs.fact.auth.title': 'Authentication',
  'docs.fact.auth.note': 'full key shown only once',
  'docs.fact.copy': 'Copy',
  'docs.section.prepare.label': 'Prepare',
  'docs.section.prepare.title': 'Before you integrate',
  'docs.section.models.label': 'Model names',
  'docs.section.models.title': 'Model names and mappings',
  'docs.section.createKey.label': 'Create a key',
  'docs.section.createKey.title': 'Create an API key',
  'docs.section.claudeCode.label': 'Claude Code',
  'docs.section.claudeCode.title': 'Claude Code setup',
  'docs.section.codex.label': 'Codex',
  'docs.section.codex.title': 'Codex setup',
  'docs.section.ccSwitch.label': 'CC Switch',
  'docs.section.ccSwitch.title': 'CC Switch setup',
  'docs.section.openclaw.label': 'OpenClaw',
  'docs.section.openclaw.title': 'OpenClaw setup',
  'docs.section.clients.label': 'Graphical clients',
  'docs.section.clients.title': 'Graphical client setup',
  'docs.section.claudeDesktop.label': 'Claude Desktop',
  'docs.section.claudeDesktop.title': 'Claude Desktop setup',
  'docs.section.vscode.label': 'VS Code',
  'docs.section.vscode.title': 'Using it in VS Code',
  'docs.section.images.label': 'GPT images',
  'docs.section.images.title': 'GPT image generation and editing',
  'docs.images.note':
    'The standard OpenAI Images protocol: `POST /v1/images/generations` to generate and ' +
    '`POST /v1/images/edits` to edit (multipart upload supported). The official SDKs work ' +
    'unchanged — point base_url at the OpenAI-compatible address above and pass the full ' +
    'image SKU name from your catalog. Generation is far slower than a text request and SDK ' +
    'default timeouts are usually too short — raise yours explicitly.',
  'docs.images.sdk': 'Official Python SDK (generate + edit)',
  'docs.images.curl': 'curl (generate + edit)',
  'docs.images.table.generate': 'POST /v1/images/generations parameters',
  'docs.images.table.edit': 'POST /v1/images/edits parameters',
  'docs.images.column.param': 'Parameter',
  'docs.images.column.required': 'Required',
  'docs.images.column.default': 'Default',
  'docs.images.column.behaviour': 'Actual behaviour',
  'docs.images.required.yes': 'Yes',
  'docs.images.required.no': 'No',
  'docs.images.default.none': 'None',
  'docs.images.param.model':
    'The full image SKU name from your catalog (`namespace/model`). A bare name works only if ' +
    'that key has a model mapping for it.',
  'docs.images.param.prompt': 'What to generate, or what to change.',
  'docs.images.param.quality':
    'Accepted but not honoured. The response reports the tier that was actually used.',
  'docs.images.param.n': 'Only 1 is supported. Anything else is a 400, and costs nothing.',
  'docs.images.param.response_format':
    'Only `b64_json`. `url` is a 400 — this route serves no hosted links.',
  'docs.images.param.background': 'Only `opaque`. `transparent` is a 400.',
  'docs.images.param.size':
    'Passed through as an aspect-ratio hint, not a constraint. The `size` in the response is what ' +
    'was DELIVERED and may differ from what you asked for. Read the response; do not assume it ' +
    'echoes the request.',
  'docs.images.param.output_format': '`png` / `jpeg` / `webp`.',
  'docs.images.param.output_compression': 'Compression quality; applies to `jpeg` / `webp` only.',
  'docs.images.param.image':
    'The image to change; several reference images are allowed. Multipart file upload and base64 ' +
    'in JSON both work. The request body is capped at 12 MB.',
  'docs.images.param.mask':
    'Marks the region to repaint. The mask guides the model rather than cutting exactly.',
  'docs.images.billing':
    'Billing follows the `usage` block on the response: `output_tokens_details.image_tokens` is ' +
    "the image output and `input_tokens` is the input side, both priced at that image SKU's " +
    'rate — so every charge reconciles against Usage and Billing line by line.',
  'docs.section.videos.label': 'Grok video',
  'docs.section.videos.title': 'Grok video generation',
  'docs.videos.note':
    'xAI video generation: `POST /v1/videos/generations` submits a job and ' +
    '`GET /v1/videos/{request_id}` polls it. Pass the full video SKU name from your catalog ' +
    '(`namespace/model`); do not send the bare official name. The Python `xai-sdk` `api_host` is ' +
    'the gateway origin WITHOUT `/v1` — the same host as the Anthropic entry above. If the SDK ' +
    'names that field differently from upstream, the test is whether the call lands on ' +
    '`POST /v1/videos/generations`. A successful create returns only `{ "request_id": "…" }`; that ' +
    'is our wrapping id, and the upstream `vid_` never appears in the response. On `done`, ' +
    '`video.url` is a temporary mp4 — download it immediately if you need to keep it. Generation ' +
    'is an order of magnitude slower than a text request, and SDK default timeouts are usually ' +
    'too short — raise yours explicitly.',
  'docs.videos.sdk': 'Python xai-sdk (create; SDK polls)',
  'docs.videos.curl': 'curl (create + poll)',
  'docs.videos.table.generate': 'POST /v1/videos/generations parameters',
  'docs.videos.column.param': 'Parameter',
  'docs.videos.column.required': 'Required',
  'docs.videos.column.default': 'Default',
  'docs.videos.column.behaviour': 'Actual behaviour',
  'docs.videos.required.yes': 'Yes',
  'docs.videos.required.no': 'No',
  'docs.videos.default.none': 'None',
  'docs.videos.param.model':
    'The full video SKU name from your catalog (`xai/grok-imagine-video` or ' +
    '`xai/grok-imagine-video-1.5`). A bare name works only if that key has a model mapping for it.',
  'docs.videos.param.prompt':
    'Text-to-video description. An empty string is a 400, and costs nothing.',
  'docs.videos.param.resolution':
    'Only `480p` / `720p`. Omitting it is a 400. `1080p` is a 400 with ' +
    '"This resolution is not available." and costs nothing. Any other value is also a 400.',
  'docs.videos.param.duration':
    'Positive integer seconds, range 1–15. Omit and the upstream default of 8 seconds applies. ' +
    'A non-integer or non-positive value is a 400, and costs nothing.',
  'docs.videos.param.image':
    'Image-to-video is not wired. Sending `image`, `image_url`, or `images` is a 400, and costs nothing.',
  'docs.videos.param.reference_images':
    'Supported and billed. 1–14 images (14 is a limit, not a promise that multiple images compose). ' +
    'Each entry is `{"url": "…"}` (a `data:` URL also works) or ' +
    '`{"data": "<base64>", "media_type": "image/png"}` (`jpeg` / `webp` / `gif` also). ' +
    'Inline images: 5 MiB decoded each, 8 MiB decoded in total; https URLs do not count. ' +
    'The request body is capped at 10 MiB. Each reference image adds a flat per-image charge at ' +
    "that model's rate — rates differ by model; see the pricing page. Over the cap or a malformed " +
    'entry is a 400, and costs nothing.',
  'docs.videos.param.reference':
    'Not wired. Sending `reference_audios` or `voices` is a 400, and costs nothing.',
  'docs.videos.param.unforwarded':
    'Not forwarded this round. Sending them is the same as omitting them.',
  'docs.videos.param.idempotency':
    'Request header. The gateway mints one if omitted. Reusing a key with a different body is a 409.',
  'docs.videos.poll':
    '`GET /v1/videos/{id}` reports `status` as `pending`, `done`, `failed`, or `expired`. A `done` ' +
    'payload carries `video.url` (a temporary mp4 — download it immediately) and `video.duration` ' +
    '(the delivered length; billing uses this, not the duration you requested). One in-flight job ' +
    'per account per resolution tier; anything above that is a 429 `TOO_MANY_TASKS_IN_FLIGHT`.',
  'docs.videos.billing':
    'Billed as delivered seconds × the per-second price of the resolution tier (`480p` / `720p`) ' +
    "plus each reference image × that model's per-image rate (rates differ by model; list prices " +
    'are on the pricing page). Jobs with no reference images are unchanged. No delivery means no ' +
    'charge: a 4xx, `failed` / `expired`, or a success with no usable url or duration. Every ' +
    'charge reconciles against the pricing page line by line.',
  'docs.section.seedance.label': 'Seedance video',
  'docs.section.seedance.title': 'Seedance video generation',
  'docs.seedance.note':
    'Ark task protocol — a different line from Grok video. Submit with `POST /v1/tasks/submit` ' +
    'and a body of `{model, input:{content:[…]}, parameters:{duration,resolution,ratio}}`; poll ' +
    'with `GET /v1/tasks/status?task_id=…`. Statuses are `Pending`, `Running`, `Success`, ' +
    '`Failure`, `Expired` (capitalized). The result is `output.urls[]` (an array). There is no ' +
    "official SDK; the snippets below are curl and Python's standard library.",
  'docs.seedance.curl': 'curl (submit + poll)',
  'docs.seedance.python': 'Python stdlib (submit + poll)',
  'docs.seedance.table.params': 'POST /v1/tasks/submit parameters',
  'docs.seedance.column.param': 'Parameter',
  'docs.seedance.column.required': 'Required',
  'docs.seedance.column.default': 'Default',
  'docs.seedance.column.behaviour': 'Actual behaviour',
  'docs.seedance.required.yes': 'Yes',
  'docs.seedance.required.no': 'No',
  'docs.seedance.default.none': 'None',
  'docs.seedance.param.duration':
    'Integer seconds. seedance-2.0: 4–15 (no 1–3s). seedance-2.5: 4–30, or `-1` (smart duration). ' +
    'Out of range is rejected before any spend. Also accepted as prompt commands `--duration` / ' +
    '`--dur`; structured fields win; a repeated command takes the last occurrence.',
  'docs.seedance.param.resolution':
    'seedance-2.0: `480p` / `720p` only; `1080p` / `4K` is a 400 before any spend. seedance-2.5: ' +
    '`480p` / `720p` / `1080p`. Out of range is rejected before any spend. Also accepted as ' +
    'prompt commands `--resolution` / `--rs`; structured fields win; a repeated command takes ' +
    'the last occurrence.',
  'docs.seedance.param.ratio':
    'String. Also accepted as prompt commands `--ratio` / `--rt`; structured fields win; a ' +
    'repeated command takes the last occurrence. The structured `parameters` object is a closed ' +
    'vocabulary of `duration`, `resolution`, and `ratio` — any other structured field is rejected ' +
    'explicitly, not silently ignored.',
  'docs.seedance.reference':
    'Reference materials are extra `input.content` members beside the text part.',
  'docs.seedance.reference.image': 'Reference image (`image_url`)',
  'docs.seedance.reference.video': 'Reference video (`video_url`)',
  'docs.seedance.artifacts':
    "`output.urls[]` are this gateway's authenticated handles. Fetch with the same bearer token. " +
    'Valid for 24 hours; expired handles cannot be refreshed — regenerate.',
  'docs.seedance.billing':
    'Token-billed, with tiers split by whether the request includes video input. A reference ' +
    'image is the same price as text-to-video; a reference video is a different tier (lower ' +
    'per-token rate, but about twice the tokens, so more expensive per job). The pricing page ' +
    'has the matrix; every charge reconciles against the pricing page line by line.',
  'docs.section.verify.label': 'Verify',
  'docs.section.verify.title': 'Verify and troubleshoot',
  'docs.prepare.console': 'Console',
  'docs.prepare.keys_page': 'Key management',
  'docs.prepare.models': 'Model list',
  'docs.prepare.requests': 'Request logs',
  'docs.prepare.warning':
    'The most common mistake is a wrong Base URL: the Anthropic endpoint must NOT include /v1, while the OpenAI-compatible endpoint MUST include /v1.',
  'docs.prepare.anthropic_entry': 'Anthropic entry · no /v1',
  'docs.prepare.anthropic_fit':
    'Claude-series models go here: Claude Code, Claude Desktop, and other Anthropic-protocol tools. Some Claude namespaces accept Claude Code clients only.',
  'docs.prepare.openai_entry': 'OpenAI entry · with /v1',
  'docs.prepare.openai_fit':
    'GPT-series models go here: OpenAI-compatible tools such as Codex, OpenClaw, Cherry Studio, ChatBox, and LobeChat.',
  'docs.prepare.env_check': 'Command-line environment check',
  'docs.models.intro':
    'Model names come in two forms: full names with a "/" (namespace/model, e.g. {example}) work on any key out of the box; short names without a "/" are looked up only in the key model-mapping table and return 404 without a matching mapping.',
  'docs.models.row.canonical': 'Full model name',
  'docs.models.row.canonical_value':
    'The "namespace/model" form, exactly as shown on the Model pricing page — copy and use as-is. Recommended for all new integrations.',
  'docs.models.row.alias': 'Short name (alias)',
  'docs.models.row.alias_value':
    'For clients that can only send bare model names. Add an "alias → target model" mapping on the key first; aliases must not contain "/".',
  'docs.models.row.manage': 'Managing mappings',
  'docs.models.row.manage_value':
    'On the Keys page, "Model mappings" can map every current model in one namespace at once or keep a namespace mapped automatically as new models appear. Conflicting short names are shown and excluded until you disable an automatic namespace or remove the conflicting manual mapping.',
  'docs.models.row.pricing': 'Price differences',
  'docs.models.row.pricing_value':
    'The same model can exist under several namespaces at different rate multipliers — picking a model name is also picking a price. See the Model pricing page.',
  'docs.models.list_snippet': 'List the models this key can call',
  'docs.models.source_of_truth':
    'GET /v1/models is the source of truth for each key: it lists every full model name visible to you plus the aliases configured on that key.',
  'docs.models.cc_lane':
    'Some Claude namespaces only accept requests from the Claude Code client — calling them via curl or graphical clients returns an upstream error. Use those models from Claude Code, or pick a namespace that supports generic API calls.',
  'docs.createKey.step1.title': 'Open the Keys page',
  'docs.createKey.step1.body': 'Sign in to the console, go to the Keys page, and click Create key.',
  'docs.createKey.step2.title': 'Optionally pre-seed mappings and limits',
  'docs.createKey.step2.body':
    'Keys are not provider-scoped. Optionally pick a namespace to atomically seed its current short-name mappings, then enable automatic mapping later if future models should appear without manual work. You can also set spend limits.',
  'docs.createKey.step3.title': 'Copy and store it',
  'docs.createKey.step3.body':
    'The full key is shown only once. Name keys after their tool to keep usage traceable.',
  'docs.createKey.row.name': 'Key name',
  'docs.createKey.row.name_value':
    'Name keys after the tool, e.g. claude-code-mac, codex-work, cherry-studio.',
  'docs.createKey.row.template': 'Namespace template',
  'docs.createKey.row.template_value':
    'Optional. Atomically seeds one "alias = model name" mapping per current model in the chosen namespace. In Model mappings, automatic mode keeps future models in sync and reports duplicate aliases before they can route ambiguously.',
  'docs.createKey.row.limits': 'Spend limits',
  'docs.createKey.row.limits_value':
    'Optional CNY caps per day, per week, and per month. They are checked against metered usage: the request that crosses a cap still completes, and the key is then paused until that period ends.',
  'docs.createKey.hint':
    'One key can call every model visible to you — Claude and GPT alike. Create separate keys per tool or member for usage attribution. If the dashboard balance is 0, ask the team owner or an admin to top up first.',
  'docs.createKey.open_keys': 'Go create a key',
  'docs.platform.aria': 'Operating system',
  'docs.platform.macos': 'macOS / Linux',
  'docs.platform.windows': 'Windows',
  'docs.claudeCode.intro':
    'Claude Code is built for reading, changing, and refactoring code from the terminal. To route it through Nxtpath, use the Anthropic entry and set the model to a full model name (namespace/model).',
  'docs.claudeCode.install': 'Install and confirm the version',
  'docs.claudeCode.temp_env': 'Temporary environment variables',
  'docs.claudeCode.long_term': 'Persistent configuration',
  'docs.claudeCode.row.model': 'Model setting',
  'docs.claudeCode.row.model_value':
    'Set ANTHROPIC_MODEL to a full model name from the Model pricing page; optionally set ANTHROPIC_SMALL_FAST_MODEL for the background model. If unset, the default model names Claude Code sends need same-named mappings on the key.',
  'docs.claudeCode.row.first_test': 'First test',
  'docs.claudeCode.row.first_test_value':
    'Open a small project directory and give Claude Code a read-only task first.',
  'docs.claudeCode.row.prompt': 'Suggested prompt',
  'docs.claudeCode.row.prompt_value':
    'Do not modify any files yet — read the project structure and tell me how to start this project.',
  'docs.claudeCode.row.url_mistake': 'Common URL mistake',
  'docs.claudeCode.row.url_mistake_value': 'Do not append /v1 to ANTHROPIC_BASE_URL.',
  'docs.claudeCode.disable_betas': 'Disable experimental beta fields',
  'docs.codex.intro':
    'Codex-style tools use the OpenAI-compatible interface. To route them through Nxtpath, set the Base URL to the OpenAI endpoint, use your Nxtpath API key, and set the model to a full model name (e.g. {example}). Both keys in config.toml must be identical; after editing, fully quit Codex / the ChatGPT desktop app (including background processes) and reopen it for the change to take effect.',
  'docs.codex.env.codex': 'Codex environment variables',
  'docs.codex.row.provider': 'Provider',
  'docs.codex.row.provider_value': 'OpenAI Compatible / Custom OpenAI',
  'docs.codex.row.base_url': 'Base URL / API Host',
  'docs.codex.row.api_key': 'API Key',
  'docs.codex.row.api_key_value': 'Your Nxtpath API key, copied from the Keys page.',
  'docs.codex.row.model': 'Model',
  'docs.ccSwitch.intro':
    'CC Switch manages providers for Claude Code, Codex, OpenClaw, and similar tools in one place. Once set up, you can flip between the Nxtpath Claude and OpenAI Compatible configs locally.',
  'docs.ccSwitch.install': 'Install on macOS',
  'docs.ccSwitch.claude_panel': 'Claude Code provider',
  'docs.ccSwitch.openai_panel': 'Codex / OpenAI provider',
  'docs.ccSwitch.row.provider_name': 'Provider name',
  'docs.ccSwitch.row.claude_provider_name': 'Nxtpath Claude',
  'docs.ccSwitch.row.openai_provider_name': 'Nxtpath OpenAI Compatible',
  'docs.ccSwitch.row.app': 'Tool / app',
  'docs.ccSwitch.row.claude_app': 'Claude Code',
  'docs.ccSwitch.row.openai_app': 'Codex / OpenClaw / OpenAI Compatible',
  'docs.ccSwitch.row.protocol': 'API protocol',
  'docs.ccSwitch.row.claude_protocol': 'Anthropic Compatible',
  'docs.ccSwitch.row.openai_protocol': 'OpenAI Compatible',
  'docs.ccSwitch.row.base_url': 'Base URL',
  'docs.ccSwitch.row.api_key': 'API Key',
  'docs.ccSwitch.row.claude_key': 'Your Nxtpath API key',
  'docs.ccSwitch.row.openai_key': 'Your Nxtpath API key (the same key as Claude works)',
  'docs.ccSwitch.row.model': 'Model',
  'docs.ccSwitch.checklist.title': 'If switching does not take effect, check',
  'docs.ccSwitch.checklist.1': 'The provider currently selected in CC Switch is the right one.',
  'docs.ccSwitch.checklist.2':
    'The terminal was opened before you switched — open a new terminal after switching.',
  'docs.ccSwitch.checklist.3': 'Whether Claude Code / Codex needs a restart.',
  'docs.ccSwitch.checklist.4':
    'The Base URL matches the protocol: Anthropic without /v1, OpenAI with /v1.',
  'docs.ccSwitch.checklist.5': 'New calls appear in the console request logs.',
  'docs.openclaw.intro':
    'OpenClaw is configured as an OpenAI-compatible provider. Point the provider at the Nxtpath OpenAI Base URL and set the model to a full model name.',
  'docs.openclaw.snippet': 'OpenAI-compatible provider',
  'docs.openclaw.row.base_url': 'Base URL',
  'docs.openclaw.row.api_key': 'API Key',
  'docs.openclaw.row.api_key_value': 'Your Nxtpath API key.',
  'docs.openclaw.row.model': 'Model',
  'docs.openclaw.row.verify': 'Verification prompt',
  'docs.openclaw.row.verify_value': 'Reply with: OpenClaw is connected.',
  'docs.clients.column.tool': 'Tool',
  'docs.clients.column.vendor': 'Protocol',
  'docs.clients.column.base': 'Base URL',
  'docs.clients.column.notes': 'Notes',
  'docs.clients.base_both': 'both protocols',
  'docs.clients.tool.claude_code': 'Terminal AI coding; uses the Anthropic entry.',
  'docs.clients.tool.codex': 'OpenAI-compatible CLI; the responses wire API is recommended.',
  'docs.clients.tool.cc_switch': 'Manages providers for Claude Code, Codex, OpenClaw, and more.',
  'docs.clients.tool.openclaw': 'Configure as an OpenAI Compatible provider.',
  'docs.clients.tool.cherry_studio':
    'Desktop client — pick models that support generic API calls (some Claude namespaces are Claude Code-only).',
  'docs.clients.tool.chatbox':
    'Lightweight chat client; set API Host to the OpenAI-compatible URL.',
  'docs.clients.tool.lobechat': 'Self-hosted or desktop — both use the OpenAI-compatible endpoint.',
  'docs.clients.tool.claude_desktop':
    'Third-party gateway entry; if calls fail with upstream errors, use Claude Code instead.',
  'docs.clients.key_name': 'Suggested key name: {name}',
  'docs.clients.row.entry': 'Settings entry',
  'docs.clients.row.provider_type': 'Provider type',
  'docs.clients.row.provider_type_value': 'OpenAI Compatible / custom OpenAI',
  'docs.clients.row.api_host': 'API host',
  'docs.clients.row.model': 'Model',
  'docs.clients.row.test': 'Test',
  'docs.clients.cherry.entry': 'Model services / provider management',
  'docs.clients.cherry.test': 'Confirm the connection works in one sentence.',
  'docs.clients.cherry.note':
    'Some Claude namespaces only accept the Claude Code client and fail with upstream errors in Cherry Studio; use the full model names shown on the Model pricing page.',
  'docs.clients.chatbox.entry': 'Model provider / API settings',
  'docs.clients.chatbox.test': 'Reply with: ChatBox is configured.',
  'docs.clients.chatbox.note':
    'Enter the full OpenAI Base URL as the API Host — do not drop or duplicate /v1.',
  'docs.clients.lobechat.entry': 'OpenAI / OpenAI Compatible',
  'docs.clients.lobechat.test': 'Reply with: LobeChat is configured.',
  'docs.clients.lobechat.note':
    'For team use, create separate keys per member or project so spend stays traceable.',
  'docs.clients.lobechat_env': 'LobeChat self-hosted environment variables',
  'docs.claudeDesktop.intro':
    'This configures the graphical third-party gateway in Claude Desktop — not the Claude Code terminal variables. If calls fail with upstream errors, the chosen model is Claude Code-only; integrate via Claude Code instead.',
  'docs.claudeDesktop.macos.title': 'macOS setup',
  'docs.claudeDesktop.macos.1': 'Open Help → Troubleshooting → Enable Developer Mode.',
  'docs.claudeDesktop.macos.2':
    'After the app restarts, keep Claude Desktop selected on the welcome screen.',
  'docs.claudeDesktop.macos.3': 'Open Developer → Configure Provider.',
  'docs.claudeDesktop.macos.4':
    'Set Gateway base URL to {baseUrl} and Gateway API key to your Nxtpath API key.',
  'docs.claudeDesktop.macos.5':
    'Click Apply locally, choose Relaunch now, then Continue with gateway after the restart.',
  'docs.claudeDesktop.windows.title': 'Windows setup',
  'docs.claudeDesktop.windows.1': 'Stay on the welcome or email-entry screen.',
  'docs.claudeDesktop.windows.2':
    'Press Tab to reach the top-left menu icon, then press Enter to open the menu.',
  'docs.claudeDesktop.windows.3':
    'Choose Developer → Configure third-party interface or Configure Provider.',
  'docs.claudeDesktop.windows.4': 'Set Gateway base URL to {baseUrl} — without /v1.',
  'docs.claudeDesktop.windows.5': 'After Apply locally, restart when prompted.',
  'docs.claudeDesktop.row.no_menu': 'Developer menu missing',
  'docs.claudeDesktop.row.no_menu_value':
    'Enable Developer Mode first and confirm Claude Desktop is the selected app.',
  'docs.claudeDesktop.row.no_gateway': 'Continue with gateway never appears',
  'docs.claudeDesktop.row.no_gateway_value': 'Confirm you clicked Apply locally and relaunched.',
  'docs.claudeDesktop.row.no_connect': 'Cannot connect after configuring',
  'docs.claudeDesktop.row.no_connect_value':
    'Check that the Gateway base URL and key are complete.',
  'docs.claudeDesktop.row.rejected': 'Requests fail with upstream errors',
  'docs.claudeDesktop.row.rejected_value':
    'The namespace of the chosen model only accepts the Claude Code client — switch to Claude Code, or pick a model that supports generic clients.',
  'docs.vscode.intro':
    'VS Code itself is not a model client, but it is a handy project entry point — run Claude Code or Codex in its built-in terminal. If an extension supports custom providers, fill in the matching protocol.',
  'docs.vscode.snippet': 'VS Code built-in terminal example',
  'docs.vscode.row.openai_base': 'OpenAI-compatible Base URL',
  'docs.vscode.row.anthropic_base': 'Anthropic-compatible Base URL',
  'docs.vscode.row.openai_models': 'OpenAI models',
  'docs.vscode.row.openai_models_value':
    'Full model names (namespace/model) as shown on the Model pricing page.',
  'docs.vscode.row.anthropic_models': 'Anthropic models',
  'docs.vscode.row.anthropic_models_value':
    'Full model names as well; some Claude namespaces are Claude Code-only, so extension calls fail with upstream errors.',
  'docs.verify.note':
    'Verify the key and its callable models first with GET /v1/models (see the Model names section), then send a real request — replace the model placeholder in the example with a name returned by /v1/models. Some Claude namespaces only accept the Claude Code client, so a plain curl against those models fails with an upstream error, which is expected.',
  'docs.verify.anthropic_curl': 'Anthropic /v1/messages',
  'docs.verify.openai_curl': 'OpenAI /v1/chat/completions',
  'docs.verify.column.symptom': 'Symptom',
  'docs.verify.column.reason': 'Likely cause',
  'docs.verify.column.action': 'What to do',
  'docs.verify.error1.symptom': '401 / unauthorized',
  'docs.verify.error1.reason': 'Wrong key, copied with whitespace, or key revoked.',
  'docs.verify.error1.action': 'Re-check the key; revoke and recreate it if needed.',
  'docs.verify.error2.symptom': '404 model not found',
  'docs.verify.error2.reason':
    'A short name without a mapping, a misspelled name, or a model not visible to you.',
  'docs.verify.error2.action':
    'Use the full model name (namespace/model) from the Model pricing page; add a mapping on the key for short names; GET /v1/models lists every name this key can call.',
  'docs.verify.error3.symptom': 'Third-party client fails on claude models',
  'docs.verify.error3.reason': 'The namespace of that model only accepts the Claude Code client.',
  'docs.verify.error3.action':
    'Switch to Claude Code, or pick a model that supports generic API calls (see the Model pricing page).',
  'docs.verify.error4.symptom': 'Endpoint unreachable / path 404',
  'docs.verify.error4.reason': 'Wrong Base URL.',
  'docs.verify.error4.action': 'Anthropic Base URL without /v1; OpenAI Base URL with /v1.',
  'docs.verify.error5.symptom': 'No calls in the console',
  'docs.verify.error5.reason': 'Requests are not going through Nxtpath.',
  'docs.verify.error5.action': 'Check the active provider, environment variables, or config file.',
  'docs.verify.error6.symptom': '402 insufficient balance',
  'docs.verify.error6.reason': 'Team balance is 0.',
  'docs.verify.error6.action': 'Ask the team owner or an admin to top up.',
  'docs.verify.error7.symptom': '403 budget limited',
  'docs.verify.error7.reason':
    'A key spend limit or member monthly limit was crossed: an earlier request took metered usage past it and the key was paused.',
  'docs.verify.error7.action':
    'Raise the relevant limit or wait for the period to reset — either restores service automatically (a raise takes effect within about ten minutes). Key limits are editable on the Keys page.',
  'docs.verify.link.usage': 'Usage',
  'docs.verify.link.usage_detail': 'Spend by key and model',
  'docs.verify.link.requests': 'Request logs',
  'docs.verify.link.requests_detail': 'Every request and its errors',
  'docs.verify.link.pricing': 'Model pricing',
  'docs.verify.link.pricing_detail': 'Confirm the available models',
  'docs.verify.security':
    'Use API keys only in your own tools. Never share them, and never paste them into public repos, screenshots, or group chats. Create one key per tool so a leak only means revoking and recreating that one.',
  'docs.served.loading': 'Loading the latest integration docs…',
  'docs.served.error': 'Could not load the latest integration docs. Showing the bundled copy.',

  // ── Usage page ─────────────────────────────────────────────────
  'usage.range.aria_label': 'Time range',
  'usage.range.today': 'Today',
  'usage.range.7d': '7d',
  'usage.range.30d': '30d',
  'usage.range.90d': '90d',
  'usage.range.prev_month': 'Previous month',
  'usage.range.next_month': 'Next month',
  'usage.range.presets_aria_label': 'Preset ranges',
  'usage.summary.requests': 'Requests',
  'usage.summary.cost': 'Cost',
  'usage.summary.loading': 'Loading',
  'usage.model_mix.title': 'By model',
  'usage.summary.tokens_in': 'Tokens in',
  'usage.summary.tokens_out': 'Tokens out',
  'usage.summary.cache_write': 'Cache write',
  'usage.summary.cache_read': 'Cache read',
  'usage.summary.blended_spend': 'Blended spend',
  // also consumed by Optimization.tsx
  'usage.summary.spend': 'Spend',
  // also consumed by Optimization.tsx
  'usage.summary.cache_hit': 'Cache hit',
  'usage.summary.tokens_value': '{tokens} tokens',
  // also consumed by Optimization.tsx
  'usage.requests_count': '{count} requests',
  'usage.succeeded_count': '{count} succeeded',
  'usage.pagination.previous': 'Previous',
  'usage.pagination.next': 'Next',
  'usage.metric.total': 'total',
  'usage.metric.in': 'In',
  'usage.metric.out': 'Out',
  'usage.metric.write': 'Cache write',
  'usage.metric.read': 'Cache read',
  'usage.error.load': 'Could not load usage data.',
  'usage.retry': 'Retry',
  'usage.admin.title': 'Token usage',
  'usage.admin.subtitle': 'Aggregate model consumption across every member, key, and request.',
  'usage.admin.window.meta': '{from} - {to} · refreshed {time}',
  'usage.admin.window.bucket_timezone': 'Buckets cut in {timezone}',
  'usage.admin.range.7d': 'Last 7 days',
  'usage.admin.range.30d': 'Last 30 days',
  'usage.admin.range.90d': 'Last 90 days',
  'usage.admin.range.month': 'This month',
  'usage.admin.filter.model': 'Model',
  'usage.admin.filter.api_key': 'API key',
  'usage.admin.filter.department': 'Org unit',
  'usage.admin.filter.member': 'Member',
  'usage.admin.filter.all_models': 'All models',
  // The selection a pre-#1672 link restores: one model name, every
  // vendor serving it. Shaped like the server's own option label
  // (`model · vendor`) so it reads as a peer of the rows around it,
  // with "all vendors" standing where a single vendor would.
  'usage.admin.filter.model_all_vendors': '{model} · all vendors',
  'usage.admin.filter.all_keys': 'All keys',
  'usage.admin.filter.all_departments': 'All org units',
  'usage.admin.filter.all_members': 'All members',
  'usage.admin.filter.show_revoked_keys': 'Show revoked keys',
  'usage.admin.filter.key_active': 'Active',
  'usage.admin.filter.key_revoked': 'Revoked',
  'usage.admin.filter.key_unknown': 'Not found',
  'usage.admin.filter.no_active_keys': 'No active API keys',
  'usage.admin.filter.group_by': 'Group by',
  'usage.admin.group_by.model': 'Model',
  'usage.admin.group_by.department': 'Org unit',
  'usage.admin.group_by.member': 'Member',
  'usage.admin.group_by_value.model': 'model',
  'usage.admin.group_by_value.department': 'org unit',
  'usage.admin.group_by_value.member': 'member',
  'usage.admin.granularity.day': 'day',
  'usage.admin.granularity.week': 'week',
  'usage.admin.granularity.month': 'month',
  'usage.admin.summary.cache_write_detail': 'prompt cache creation',
  'usage.admin.summary.cache_read_detail': 'prompt cache reuse',
  'usage.admin.summary.cost_detail': 'usage logs cost metadata',
  'usage.admin.summary.unit_billing_detail':
    'Video seconds, images and other unit-billed usage. Not counted as tokens.',
  // Two calibers, never one ratio: the headline counts what moved
  // inside the selected window, the detail counts what exists right
  // now. A seat or key can appear in the window and be gone from the
  // roster, so the first number can exceed the second — "of" phrasing
  // would turn that into an impossible-looking fraction.
  'usage.admin.summary.seats_with_usage': 'Seats with usage',
  'usage.admin.summary.seats_roster_detail': 'Roster now: {seats} seats',
  'usage.admin.summary.keys_with_usage': 'Keys with usage',
  'usage.admin.summary.keys_roster_detail': 'Roster now: {keys} not revoked',
  // Only reachable against a platform-api older than router#1681, which
  // sends no roster key count at all. Drop with `usage/calibers.ts`.
  'usage.admin.summary.keys_roster_unknown': 'Roster now: unavailable',
  'usage.admin.trend.title': 'Token usage trend',
  'usage.admin.trend.subtitle': 'Stacked by {groupBy}',
  'usage.admin.trend.hint': 'Hover any bar to inspect a single window',
  'usage.admin.trend.total': 'total · {tokens} tokens',
  'usage.admin.trend.incomplete': 'partial bucket',
  'usage.admin.trend.incomplete_note': 'Faded bars only partly overlap the selected window.',
  'usage.admin.empty.window': 'No usage in this window.',
  'usage.admin.model_mix.subtitle': 'Share of total tokens in the selected window.',
  'usage.admin.model_mix.subtitle_capped':
    'Share of total tokens in the selected window. The top {count} are named; the rest are summed as Other.',
  'usage.admin.model_mix.empty': 'No model usage in this window.',
  'usage.admin.model_mix.io': 'in {input} · out {output} · cache write {write} · cache read {read}',
  'usage.admin.model_mix.other': 'Other',
  // The commonest overflow is by exactly one model, so `{count} models`
  // printed `1 models` on the most likely path (#54). This catalog has
  // no plural rule and is not getting one; the wording is chosen to not
  // need one.
  'usage.admin.model_mix.other_models': '+{count} more',
  'usage.admin.department_mix.title': 'Org unit mix',
  'usage.admin.department_mix.subtitle': 'Tokens consumed per org unit this period.',
  'usage.admin.department_mix.empty': 'No org unit usage in this window.',
  'usage.admin.department_mix.unassigned': 'Unassigned',
  'usage.admin.service.title': 'Service health',
  'usage.admin.service.subtitle': 'Request quality based on billing metadata.',
  'usage.admin.service.all_green': 'All green',
  'usage.admin.service.rate_limited': 'Rate-limited',
  'usage.admin.service.cache_hit_rate': 'Cache hit rate',
  'usage.admin.service.requests_evaluated': 'Requests evaluated',
  'usage.admin.service.avg_latency': 'Avg latency',
  'usage.admin.service.recovery_events': 'Recovery events',
  'usage.admin.seat.title': 'Usage by seat',
  'usage.admin.seat.subtitle': '{count} members · sorted by total tokens',
  'usage.admin.seat.search_placeholder': 'Search name, email, org unit...',
  'usage.admin.seat.search_label': 'Search seats',
  'usage.admin.seat.empty': 'No member usage matches this window.',
  'usage.admin.seat.column.member': 'Member',
  'usage.admin.seat.column.team': 'Org unit',
  'usage.admin.seat.column.primary_model': 'Primary model',
  'usage.admin.seat.column.avg_per_request': 'Avg / req',
  'usage.admin.seat.column.monthly_cap': 'Monthly cap',
  'usage.admin.seat.column.trend_14d': '14d trend',
  'usage.admin.seat.column.cost_scoped': 'Cost · selected range',
  'usage.admin.seat.column.monthly_scoped': 'Monthly cap · calendar month',
  'usage.admin.seat.group.identity': 'Seat',
  'usage.admin.seat.group.range': 'Selected range',
  'usage.admin.seat.group.month': 'Calendar month',
  'usage.admin.seat.group.trend': 'Last 14 days',
  'usage.admin.seat.monthly.no_limit': 'No monthly cap',
  'usage.admin.seat.monthly.window_note':
    'The monthly cap runs against {from} through {to} ({zone}), month to date. Every other column follows the selected range.',
  'usage.admin.seat.status.disabled': 'Disabled',
  'usage.admin.seat.status.deleted': 'Removed',
  'usage.admin.seat.range': 'Showing {from}-{to} of {total}',
  'usage.admin.seat.pagination_aria': 'Seat pages',
  'usage.member.title': 'My token usage',
  'usage.member.subtitle': 'Your own usage, budget metadata, API keys, and request billing logs.',
  'usage.member.summary.scope_detail': '{range} personal scope',
  'usage.member.summary.cache_write_detail': '{range} cache creation',
  'usage.member.summary.cache_read_detail': '{range} cache reuse',
  'usage.member.summary.spend_detail': 'shown from billing metadata',
  'usage.member.summary.cache_hit_detail': '{rate} token hit · {count} ok',
  'usage.member.trend.title': 'Personal token trend',
  'usage.member.trend.subtitle': 'Input and output tokens from your own keys.',
  'usage.member.keys.title': 'My API keys',
  'usage.member.keys.subtitle': 'Usage generated by your own keys.',
  'usage.member.keys.empty': 'No key usage in this window.',
  'usage.member.keys.activity': '{ok} ok · {hits} cache hits',
  'usage.member.keys.cache_rates': 'Req {request} · Token {token} · R/W {ratio}',
  'usage.member.requests.title': 'Recent requests',
  'usage.member.requests.subtitle':
    'Billing metadata only; prompts and responses are not collected.',
  'usage.member.requests.empty': 'No request metadata in this window.',
  'usage.member.requests.status': 'Status {code}',

  // ── Organization page ──────────────────────────────────────────
  'organization.title': 'Organization',
  'organization.subtitle': 'Assign members to org units; usage views use this assignment first.',
  'organization.forbidden': 'Only owners and admins can view organization management.',
  'organization.loading': 'Loading organization data…',
  'organization.refresh': 'Refresh',
  'organization.retry': 'Retry',
  'organization.tab.members': 'Members',
  'organization.tab.structure': 'Structure',
  'organization.tab.permissions': 'Roles and permissions',
  'organization.tab.model_permissions': 'Model permissions',
  'organization.tab.invitations': 'Invitations',
  'organization.tab.governance': 'Governance',
  'organization.tab.audit': 'Audit',
  'organization.summary.members': 'Members',
  'organization.summary.units': 'Org units',
  'organization.summary.unassigned': 'Unassigned',
  'organization.summary.pending': 'Pending invites',
  'organization.filter.search_placeholder': 'Search name, email, org unit',
  'organization.filter.all_units': 'All org units',
  'organization.filter.unassigned': 'Unassigned',
  'organization.column.member': 'Member',
  'organization.column.unit': 'Org unit',
  'organization.column.role': 'Role',
  'organization.column.access': 'Monthly limit',
  'organization.column.permissions': 'Model permissions',
  'organization.column.keys': 'API keys',
  'organization.column.joined': 'Joined',
  'organization.column.status': 'Status',
  'organization.role.owner': 'Owner',
  'organization.role.admin': 'Admin',
  'organization.role.member': 'Member',
  'organization.role.finance': 'Finance',
  'organization.access.unrestricted': 'No limit',
  'organization.access.limited': 'Capped',
  'organization.access.amount': '{amount} / month',
  'organization.action.create': 'Create',
  'organization.action.save': 'Save',
  'organization.action.cancel': 'Cancel',
  'organization.action.edit': 'Edit',
  'organization.action.archive': 'Archive',
  'organization.error.members': 'Could not load members',
  'organization.error.units': 'Could not load org units',
  'organization.error.invitations': 'Could not load invitations',
  'organization.error.keys': 'Could not load API keys',
  'organization.members.empty': 'No members yet.',
  'organization.members.toast.unit_updated': 'Department updated',
  'organization.members.error.unit_update': 'Could not update department',
  'organization.members.toast.role_updated': 'Role updated',
  'organization.members.error.role_update': 'Could not update role',
  'organization.members.role.self_lockout':
    'A role without member management would lock you out of this page.',
  'organization.members.toast.disabled': 'Member disabled',
  'organization.members.toast.enabled': 'Member enabled',
  'organization.members.error.disable': 'Could not disable {member}',
  'organization.members.error.enable': 'Could not enable {member}',
  'organization.members.error.status_cause': '{member}: {detail}',
  'organization.members.status.disabled': 'Disabled',
  'organization.members.status.disable': 'Disable',
  'organization.members.status.enable': 'Enable',
  'organization.members.status.disable_aria': 'Disable {member}',
  'organization.members.status.enable_aria': 'Enable {member}',
  'organization.members.status.disable_title': 'Disable member',
  'organization.members.status.disable_body':
    'Disabling {member} rejects their API keys immediately, so the keys can no longer be called or incur charges, and the member loses access to this organization. Data is kept and can be restored by enabling the member again.',
  'organization.members.status.disable_confirm': 'Confirm disable',
  'organization.members.status.disable_close': 'Close disable dialog',
  'organization.members.limit.set': 'Set',
  'organization.members.limit.edit_aria': 'Set monthly limit for {member}',
  'organization.members.limit.title': 'Monthly limit',
  'organization.members.limit.description':
    'Set a monthly RMB amount for {member}. Leave the field empty for no limit.',
  'organization.members.limit.label': 'Amount (RMB)',
  'organization.members.limit.clear': 'Remove limit',
  'organization.members.limit.toast.updated': 'Monthly limit updated',
  'organization.members.limit.error.update': 'Could not update the monthly limit',
  'organization.members.limit.error.invalid':
    'The monthly limit must be a non-negative RMB amount.',
  'organization.members.limit.error.zero':
    'A limit of 0 is not allowed. Leave the field empty for no limit, or disable the member to stop their spending.',
  'organization.members.keys.loading': 'Loading',
  'organization.members.keys.owner_note': 'Managed on the API keys page',
  'organization.members.keys.manage': "View {member}'s API keys",
  'organization.members.keys.dialog_title': "{member}'s API keys",
  'organization.members.keys.dialog_subtitle': 'Active keys assigned to this member.',
  'organization.members.keys.empty': 'This member has no active API keys.',
  'organization.members.keys.column.name': 'Name',
  'organization.members.keys.column.prefix': 'Prefix',
  'organization.members.keys.column.created': 'Created',
  'organization.members.keys.column.last_used': 'Last used',
  'organization.members.keys.column.actions': 'Actions',
  'organization.members.keys.never_used': 'Never used',
  'organization.members.keys.close': 'Close',
  'organization.members.keys.revoke': 'Revoke',
  'organization.members.keys.revoke_title': 'Revoke API key',
  'organization.members.keys.revoke_body':
    'Revoking this key immediately disables it. Clients must switch to a replacement key.',
  'organization.members.keys.revoke_type_label': 'Type the key name to confirm revocation',
  'organization.members.keys.revoke_confirm': 'Confirm revoke',
  'organization.members.keys.revoke_error': 'Could not revoke API key',
  'organization.members.keys.toast.revoked': 'API key revoked',
  'organization.detail.title': 'Org unit detail',
  'organization.detail.empty': 'Select an org unit to inspect assignments.',
  'organization.detail.parent': 'Parent',
  'organization.detail.direct_members': 'Direct members',
  'organization.detail.status': 'Status',
  'organization.structure.title': 'Structure',
  'organization.structure.subtitle': 'Review org units by path and direct member count.',
  'organization.structure.empty': 'No org units yet.',
  'organization.structure.create_title': 'New org unit',
  'organization.structure.name': 'Name',
  'organization.structure.name_placeholder': 'Engineering',
  'organization.structure.parent': 'Parent',
  'organization.structure.root_parent': 'Top-level org unit',
  'organization.structure.name_required': 'Enter an org unit name',
  'organization.structure.status.active': 'Active',
  'organization.structure.status.archived': 'Archived',
  'organization.structure.error.create': 'Could not create the org unit',
  'organization.structure.error.save': 'Could not save the org unit',
  'organization.structure.error.archive': 'Could not archive the org unit',
  'organization.structure.toast.created': 'Org unit created',
  'organization.structure.toast.saved': 'Org unit updated',
  'organization.structure.toast.archived': 'Org unit archived',
  'organization.structure.archive_title': 'Archive org unit',
  'organization.structure.archive_body':
    'After archiving {unit}, it can no longer be used for member assignment.',
  'organization.structure.archive_confirm': 'Confirm archive',
  'organization.structure.archive_close': 'Close archive dialog',
  // Accessible names for the per-row icon buttons. One row's "Archive"
  // has to be distinguishable from the next one's, so each carries the
  // unit path (DESIGN.md §9).
  'organization.structure.action.save': 'Save {unit}',
  'organization.structure.action.cancel': 'Cancel editing {unit}',
  'organization.structure.action.edit': 'Edit {unit}',
  'organization.structure.action.archive': 'Archive {unit}',
  'organization.permissions.members.title': 'Members',
  'organization.permissions.members.empty': 'No members yet',
  'organization.permissions.members.empty_hint': 'Invite members before configuring model access.',
  'organization.permissions.units.title': 'Org units',
  'organization.permissions.units.empty': 'No org units yet',
  'organization.permissions.units.empty_hint':
    'Create an org unit to set model access for a whole team.',
  'organization.permissions.configure': 'Configure',
  'organization.permissions.mode.inherit': 'Inherit',
  'organization.permissions.mode.all': 'Allow all',
  'organization.permissions.mode.custom': 'Custom',
  'organization.roles.title': 'Roles',
  'organization.roles.subtitle':
    'Builtin roles are locked. Custom roles combine the closed permission list.',
  'organization.roles.degraded':
    'Role API is not available yet. Builtin roles are shown from the local fallback; custom roles cannot be edited.',
  'organization.roles.builtin': 'Built-in',
  'organization.roles.custom': 'Custom',
  'organization.roles.members_count': '{count} members',
  'organization.roles.create_name': 'Role name',
  'organization.roles.create_name_placeholder': 'Ops',
  'organization.roles.create': 'New role',
  'organization.roles.name_required': 'Enter a role name',
  'organization.roles.rename': 'Rename {name}',
  'organization.roles.delete': 'Delete {name}',
  'organization.roles.delete_title': 'Delete role',
  'organization.roles.delete_body': 'Delete {name}. Reassign members on this role before deleting.',
  'organization.roles.delete_confirm': 'Confirm delete',
  'organization.roles.delete_close': 'Close delete dialog',
  'organization.roles.toast.created': 'Role created',
  'organization.roles.toast.updated': 'Role updated',
  'organization.roles.toast.deleted': 'Role deleted',
  'organization.roles.toast.renamed': 'Role renamed',
  'organization.roles.error.create': 'Could not create the role',
  'organization.roles.error.save': 'Could not save the role',
  'organization.roles.error.delete': 'Could not delete the role',
  'organization.roles.error.rename': 'Could not rename the role',
  'organization.roles.save_permissions': 'Save permissions',
  'organization.roles.readonly': 'Builtin roles cannot be edited.',
  'organization.roles.checklist.title': 'Permissions',
  'organization.roles.group.members': 'Members',
  'organization.roles.group.keys': 'Keys',
  'organization.roles.group.billing': 'Balance & billing',
  'organization.roles.group.usage': 'Usage and request logs',
  'organization.roles.group.audit': 'Audit',
  'organization.roles.group.reports': 'Data export',
  'organization.roles.perm.members_read': 'View',
  'organization.roles.perm.members_manage': 'Manage',
  'organization.roles.perm.invitations_manage': 'Manage invitations',
  'organization.roles.perm.api_keys_read': 'View',
  'organization.roles.perm.api_keys_manage': 'Manage',
  'organization.roles.perm.billing_read': 'View',
  'organization.roles.perm.billing_manage': 'Manage',
  'organization.roles.perm.usage_read': 'View',
  'organization.roles.perm.audit_read': 'View',
  'organization.roles.perm.reports_export': 'Export',
  'organization.roles.empty_custom': 'No custom roles yet.',
  'organization.invitations.title': 'Pending invitations',
  'organization.invitations.subtitle': 'Valid invites that have not been accepted or revoked.',
  'organization.invitations.empty': 'No pending invitations.',
  'organization.invitations.empty_hint':
    'Send an invitation and it appears here until it is accepted or expires.',
  'organization.invitations.empty_hint_readonly':
    'Owners and admins can send invitations from here.',
  'organization.invitations.invite': 'Invite member',
  'organization.invitations.revoke': 'Revoke',
  'organization.invitations.revoke_title': 'Revoke invitation',
  'organization.invitations.revoke_body':
    'After revoking, {email} can no longer join through this invite link.',
  'organization.invitations.revoke_confirm': 'Confirm revoke',
  'organization.invitations.revoke_close': 'Close revoke dialog',
  'organization.invitations.revoke_error': 'Could not revoke invitation',
  'organization.invitations.toast.revoked': 'Invitation revoked',
  'organization.governance.title': 'Governance overview',
  'organization.governance.subtitle': 'Assignment coverage, limits, and manager scope.',
  'organization.governance.assigned': 'Assigned members',
  'organization.governance.limited': 'Members with a cap',
  'organization.governance.managers': 'Owners / admins',
  'organization.audit.title': 'Audit log',
  'organization.audit.subtitle':
    'Trace member, invite, model-permission, API key, and org-structure changes.',
  'organization.audit.filter.caption': 'Filters',
  'organization.audit.filter.action_label': 'Action',
  'organization.audit.filter.action_placeholder':
    'Filter by action, e.g. member_model_permissions.updated',
  'organization.audit.filter.target_label': 'Target account',
  'organization.audit.filter.target_placeholder': 'Filter by target account id',
  'organization.audit.filter.clear': 'Clear',
  'organization.audit.error.load': 'Could not load audit events',
  'organization.audit.retry': 'Retry',
  'organization.audit.empty': 'No audit events yet.',
  'organization.audit.empty_hint':
    'Member, invite, API key, model-permission, and org-structure changes appear here.',
  'organization.audit.column.time': 'Time',
  'organization.audit.column.actor': 'Actor',
  'organization.audit.column.action': 'Action',
  'organization.audit.column.target': 'Target account',
  'organization.audit.column.detail': 'Metadata',
  'organization.audit.actor_system': 'system',
  'organization.audit.pagination.previous': 'Previous',
  'organization.audit.pagination.next': 'Next',
  'organization.audit.pagination.aria': 'Audit log pagination',

  // ── Request logs page ─────────────────────────────────────────
  'requests.title': 'Request logs',
  'requests.subtitle': 'Inspect each request by status, model, tokens, cache, and cost.',
  'requests.range.aria_label': 'Request log time range',
  'requests.filter.request_id': 'Request ID',
  'requests.filter.request_id_placeholder': 'Exact request id',
  'requests.filter.key': 'Key',
  'requests.filter.key_all': 'All of my keys',
  'requests.filter.key_all_org': 'All keys in the organization',
  'requests.filter.status': 'Status',
  'requests.filter.status_all': 'All statuses',
  'requests.filter.status_success': 'Success',
  'requests.filter.status_error': 'Error',
  'requests.filter.submit': 'Search',
  'requests.filter.clear': 'Clear',
  'requests.loading': 'Loading request logs…',
  'requests.error': 'Could not load request logs.',
  'requests.retry': 'Retry',
  'requests.empty.title': 'No requests in this window',
  'requests.empty.hint':
    'Point a client at Nxtpath and send your first request — every call shows up here with its tokens and price.',
  'requests.empty.docs': 'View integration docs',
  'requests.empty.filtered.title': 'No matching requests',
  'requests.empty.filtered.hint': 'Try loosening or clearing the filters.',
  'requests.column.time': 'Time',
  'requests.column.request': 'Request ID',
  'requests.column.key': 'Key name',
  // also consumed by Optimization.tsx
  'requests.column.model': 'Model',
  'requests.modality.chat': 'Chat',
  'requests.modality.image': 'Image',
  'requests.modality.video': 'Video',
  'requests.column.status': 'Status',
  'requests.column.duration': 'Duration',
  'requests.column.first_byte': 'First byte',
  'requests.column.input': 'Input',
  'requests.column.output': 'Output',
  'requests.column.cache_read': 'Cache hit',
  'requests.column.cache_write': 'Cache write',
  'requests.column.cost': 'Cost',
  'requests.cost.pending': 'Rating...',
  'requests.cost.not_charged.not_delivered': 'Not charged',
  'requests.cost.not_charged.pricing_review': 'Under review',
  'requests.duration.ms': '{value} ms',
  'requests.detail.request_id': 'Full request ID',
  'requests.detail.copy_request_id': 'Copy request ID',
  'requests.detail.api_key_id': 'Key ID',
  'requests.detail.protocol': 'Protocol',
  'requests.detail.billing': 'Billing',
  'requests.detail.billing.billed': 'Billed',
  'requests.detail.billing.not_billed': 'Not billed',
  'requests.detail.billing.pending': 'Rating in progress',
  'requests.detail.unit_billing': 'Unit billing',
  'requests.unit_billing.charged': '{quantity} {unit} · {amount}',
  'requests.unit_billing.unit.call': 'calls',
  'requests.unit_billing.unit.second': 'seconds',
  'requests.unit_billing.unit.character': 'characters',
  'requests.unit_billing.unit.image': 'images',
  'requests.unit_billing.not_charged.not_delivered': 'Not delivered — not charged',
  'requests.unit_billing.not_charged.pricing_review': 'Under billing review',
  'requests.unit_billing.pending_breakdown':
    'Unit-metered; component breakdown not yet available on this surface.',
  'requests.token_billing.column.dimension': 'Dimension',
  'requests.token_billing.column.quantity': 'Quantity',
  'requests.token_billing.column.list_rate': 'List rate',
  'requests.token_billing.column.amount': 'Amount',
  'requests.token_billing.dimension.input': 'Input',
  'requests.token_billing.dimension.output': 'Output',
  'requests.token_billing.dimension.cache_read': 'Cache read',
  'requests.token_billing.dimension.cache_write': 'Cache write',
  'requests.token_billing.dimension.cache_write_5m': 'Cache write (5 min)',
  'requests.token_billing.dimension.cache_write_1h': 'Cache write (1 hour)',
  'requests.token_billing.dimension.reasoning': 'Reasoning',
  'requests.token_billing.dimension.image_input': 'Image input',
  'requests.token_billing.multiplier':
    'Effective multiplier ×{multiplier} (includes contract rate and any sale-window discount)',
  'requests.surcharges.title': 'Surcharges',
  'requests.detail.error_category': 'Category',
  'requests.detail.filter_by_key': 'Filter by this key',
  'requests.detail.view_key_usage': 'View key usage',
  'requests.status_category.success': 'Success',
  'requests.status_category.request_error': 'Request error',
  'requests.status_category.rate_limited': 'Rate limited',
  'requests.status_category.service_error': 'Service error',
  'requests.pagination.summary': '{from}–{to} / {total} total',
  'requests.pagination.prev': 'Newer',
  'requests.pagination.next': 'Older',
  'requests.archived.badge': 'Archived',
  'requests.archived.detail_placeholder':
    'Billing itemisation for this archived request is unavailable.',
  'requests.archived.page_errors':
    'Billing itemisation could not be loaded for some archived requests on this page.',

  // ── Optimization hub ───────────────────────────────────────────
  'optimization.title': 'Optimization Hub',
  'optimization.subtitle':
    'Turn your usage logs, cache telemetry, and model catalog into practical cost controls.',
  'optimization.window': '30-day optimization window',
  'optimization.metric.spend': '30-day spend',
  'optimization.metric.loading': 'Loading usage',
  'optimization.metric.token_hit': '{rate} token hit',
  'optimization.metric.cache_ratio': 'Cache read / write',
  'optimization.metric.read_write_detail': '{read} read · {write} write',
  'optimization.metric.input_volume': 'Input volume',
  'optimization.metric.output_tokens': '{tokens} output tokens',
  'optimization.recommendations.title': 'Optimization queue',
  'optimization.recommendations.subtitle':
    'Ranked from usage logs, cache telemetry, and the live catalog.',
  'optimization.recommendations.prefix.title': 'Stabilize prompt prefixes',
  'optimization.recommendations.prefix.body_empty':
    'Start by keeping system instructions and stable repo context before user-specific inputs.',
  'optimization.recommendations.prefix.body':
    '{key} has {hit} token cache hit and {ratio} read/write. Move dynamic content below stable context.',
  'optimization.recommendations.split.title': 'Separate long outputs from classification prompts',
  'optimization.recommendations.split.body':
    '{tokens} cache-write tokens only pay back when later calls read them. Split verbose generation from repeated classification calls.',
  'optimization.recommendations.model.title': 'Default routine work to a cache-capable model',
  'optimization.recommendations.model.body':
    '{model} exposes prompt-cache pricing in the live catalog. Keep Opus-class work for architecture and review gates.',
  'optimization.model_lab.title': 'Model fit lab',
  'optimization.model_lab.subtitle': 'Cache-capable models sorted by effective input rate.',
  'optimization.model_lab.empty': 'No cache-capable models are available in this catalog.',
  'optimization.model_lab.meta': '{window} context · cache read {rate}',
  'optimization.model_lab.input_unit': 'input / MTok',
  'optimization.model_lab.context_unknown': 'unknown',
  'optimization.simulator.title': 'Cost simulator',
  'optimization.simulator.subtitle': 'Live catalog pricing with prompt-cache assumptions.',
  'optimization.simulator.input_tokens': 'Input tokens',
  'optimization.simulator.cacheable_prefix': 'Cacheable prefix',
  'optimization.simulator.cached_percent': '{percent}% cached',
  'optimization.simulator.naive': 'Current pattern',
  'optimization.simulator.cached': 'With cache',
  'optimization.simulator.savings': 'Savings',
  'optimization.simulator.no_catalog': 'Load the catalog to simulate pricing.',
  'optimization.simulator.using_model': 'Using {model} at the best visible catalog rate.',
  'optimization.template.title': 'Context hygiene template',
  'optimization.template.subtitle': 'Keep low-signal files out of agent context.',
  'optimization.template.group.node': 'Node / web',
  'optimization.hotspots.title': 'Request hotspots',
  'optimization.hotspots.subtitle': 'Recent expensive calls worth inspecting first.',
  'optimization.hotspots.empty': 'No recent request logs in this window.',
  // Deliberately NOT `requests.column.request`: that key reads "Request ID",
  // and reusing it would smuggle a copy change into an i18n refactor.
  'optimization.hotspots.column.request': 'Request',
  'optimization.hotspots.column.tokens': 'Tokens',
  'optimization.hotspots.column.cache': 'Cache',
  'optimization.hotspots.cache_cell': 'R {read} / W {write}',

  // ── Pricing page ──────────────────────────────────────────────
  'pricing.title': 'Model pricing',
  // #1706: with unit-metered models in the catalog, the subtitle no longer
  // claims the per-Mtok dimension for the whole page.
  'pricing.subtitle': 'Compare effective prices by provider, channel, and model.',
  'pricing.unit': 'per 1M tokens · CNY',
  // #1706: neutral badge for groups containing unit-metered rows; each unit
  // line names its own dimension (Per image / Per call / …).
  'pricing.unit.neutral': 'Prices · CNY',
  'pricing.loading': 'Loading model pricing…',
  'pricing.error': 'Could not load model pricing.',
  'pricing.retry': 'Retry',
  'pricing.empty': 'No model pricing is ready to display yet.',
  'pricing.no_results': 'No model prices match the current filters.',
  'pricing.vendor.summary': '{models} models · {channels} channels',
  'pricing.vendor.no_channels': 'This provider has no available channels right now.',
  'pricing.filter.all_vendors': 'All providers',
  'pricing.filter.all_channels': 'All channels',
  'pricing.search.placeholder': 'Search models…',
  'pricing.column.model': 'Model',
  'pricing.cell.unavailable': 'Unavailable',
  'pricing.rate.input': 'Input',
  'pricing.rate.output': 'Output',
  'pricing.rate.cache_read': 'Cache hit',
  'pricing.rate.cache_write': 'Cache write',
  'pricing.rate.cache_write_5m': 'Cache write 5m',
  'pricing.rate.cache_write_1h': 'Cache write 1h',
  'pricing.rate.not_billed': 'Not billed',
  // #1706 — per-unit price labels for unit-metered SKUs; unit nouns are the
  // same closed set as requests.unit_billing.unit.*.
  'pricing.rate.unit.call': 'Per call',
  'pricing.rate.unit.second': 'Per second',
  'pricing.rate.unit.character': 'Per character',
  'pricing.rate.unit.image': 'Per image',
  'pricing.rate.unit.pending': 'Pricing pending',
  'pricing.rate.reasoning': 'Reasoning',
  // tenant#138 — eighth token-card bucket; omitted on the wire when the
  // SKU has no image-input rate. The ticket tk_59ef52d2831fa277 bill was
  // this row missing from the card.
  'pricing.rate.image_input': 'Image input',
  // Epic #898 P6 (#912) — namespace-grouped SKU market view.
  'pricing.filter.all_namespaces': 'All namespaces',
  'pricing.namespace.summary': '{skus} models',
  'pricing.column.rate': 'Rate',
  'pricing.column.prices': 'Prices',
  'pricing.sku.discount': 'Discount',
  // tenant#117 — self-describing billing document renderer. Tier, axis and
  // unit words come from the backend document's bilingual labels; these two
  // are only the frontend frame: the per-unit template around a
  // backend-issued unit noun, and the conservative placeholder shown when
  // the document's presentation version (or a rates member) is newer than
  // this reader — never a price, never a guess.
  'pricing.billing.per_unit': 'Per {unit}',
  'pricing.billing.fallback': 'See pricing notes',
  // tenant#138 — catalog billing document extras. Additive members do
  // not bump presentation_version; these are the frame words around
  // backend-issued labels and numbers. Iron rule: unrecognized shapes
  // render nothing, never a guessed price.
  'pricing.billing.prompt_tier.base': 'Base',
  'pricing.billing.prompt_tier.long': 'Long prompt ≥ {n} tokens',
  'pricing.billing.prompt_tier.basis.varies_by_ingress':
    'Tier threshold basis depends on the endpoint: Anthropic counts input + cache; OpenAI counts input (cache reads included)',
  'pricing.billing.prompt_tier.basis.input_tokens':
    'Tier threshold is input tokens (cache reads included)',
  'pricing.billing.prompt_tier.basis.input_plus_cache_tokens':
    'Tier threshold is input + cache read + cache write',
  'pricing.billing.surcharges': 'Surcharges',
  'pricing.billing.sale_windows': 'Sale windows · {timezone}',
  'pricing.billing.sale_windows.stacking':
    'Effective multiplier in a matched window = list rate × sale multiplier',
  'pricing.billing.sale.day.mon': 'Monday',
  'pricing.billing.sale.day.tue': 'Tuesday',
  'pricing.billing.sale.day.wed': 'Wednesday',
  'pricing.billing.sale.day.thu': 'Thursday',
  'pricing.billing.sale.day.fri': 'Friday',
  'pricing.billing.sale.day.sat': 'Saturday',
  'pricing.billing.sale.day.sun': 'Sunday',

  // ── Billing page ───────────────────────────────────────────────
  'billing.title': 'Balance & billing',
  'billing.subtitle': 'Check your balance, top-ups, credits, and ledger history.',
  'billing.balance.title': 'Current balance',
  'billing.balance.status.funded': 'Balance available',
  'billing.balance.status.zero': 'Needs top-up',
  'billing.balance.status.overdrawn': 'Overdrawn',
  'credit.limit': 'Limit',
  'credit.used': 'Used',
  'credit.remaining': 'Remaining',
  'credit.available': 'Available',
  'credit.overlimit': 'Over limit',
  'credit.grace': 'Payment terms',
  'credit.grace.unset': 'Not set',
  'credit.grace.days': '{days} days',
  'credit.due.current': 'Current due date',
  'credit.due.next': 'Next due date',
  'credit.stage.normal': 'Normal',
  'credit.stage.frozen': 'Credit frozen (usage unaffected)',
  'credit.stage.stop_notice': 'Stop notice',
  'credit.stage.stopped': 'Service stopped',
  'credit.stage.collections': 'In collections',
  'credit.service_stopped': 'Service stopped',
  'credit.new_frozen': 'New credit frozen',
  'credit.temp.window': 'Temporary credit {start} to {end}',
  'credit.temp.oneshot': 'Bills once on expiry',
  'credit.oneshot.open': 'A one-shot bill is open',
  'credit.exemption': 'Exempt until {date}',
  'credit.residual.title': 'Outstanding debt',
  'credit.residual.debt': 'Debt {amount}',
  'credit.residual.due': 'Due {date}',
  'credit.residual.overdue': '{days} days overdue',
  'credit.hero.line': 'Credit {limit} · used {used}',
  'credit.hero.due': 'due {date}',
  'credit.hero.residual': 'Debt {amount} · {overdue} days overdue',
  'credit.rate.locked': 'Locked rate {rate}',
  'billing.balance.loading': 'Loading balance…',
  'billing.balance.error': 'Could not load balance.',
  'billing.balance.updated': 'Updated {timestamp}',
  'billing.gift.included': 'Includes {amount} gift credit',
  'billing.gift.partial': 'of which {amount} {when}',
  'billing.gift.expires_in_days': 'expires in {days} days',
  'billing.gift.expires_in_day': 'expires in 1 day',
  'billing.gift.expires_today': 'expires today',
  'billing.gift.expired': 'expired, pending reclaim',
  'billing.credits.title': 'Balance breakdown',
  'billing.credits.subtitle':
    'Split by credit batch. Charges consume top to bottom: expiring gift credit first, then paid balance.',
  'billing.credits.error': 'Could not load the balance breakdown.',
  'billing.credits.empty': 'No credit batches available.',
  'billing.credits.column.kind': 'Source',
  'billing.credits.column.remaining': 'Remaining',
  'billing.credits.column.amount': 'Credited',
  'billing.credits.column.expires': 'Expires',
  'billing.credits.column.issued': 'Credited at',
  'billing.credits.kind.promo': 'Gift',
  'billing.credits.kind.cash': 'Paid',
  'billing.credits.never_expires': 'Never expires',
  'billing.forbidden.title': 'Billing is managed by your org admin',
  'billing.forbidden.body':
    'Members can manage their own API keys and usage, but enterprise balance, top-ups, credits, and ledger history require admin billing access.',
  'billing.forbidden.cta': 'Back to dashboard',
  'billing.retry': 'Retry',
  'billing.topup.entry': 'Top up',
  'billing.redeem.title': 'Redeem gift card',
  'billing.redeem.code_label': 'Gift-card code',
  'billing.redeem.code_placeholder': '32-character hex code',
  'billing.redeem.submit': 'Redeem',
  'billing.redeem.submitting': 'Redeeming…',
  'billing.redeem.error.code_required': 'Gift-card code is required',
  'billing.redeem.toast.success': 'Credited {amount}, balance {balance}',
  'billing.transactions.title': 'Balance history',
  'billing.transactions.latest': 'Latest',
  'billing.transactions.offset': 'Starting from item {offset}',
  'billing.transactions.page': 'Page {page}',
  'billing.transactions.loading': 'Loading transactions…',
  'billing.transactions.error': 'Could not load transactions.',
  'billing.transactions.empty': 'No transactions yet.',
  'billing.transactions.column.date': 'Date',
  'billing.transactions.column.type': 'Type',
  'billing.transactions.column.amount_usd': 'Amount (CNY)',
  'billing.transactions.column.running': 'Balance',
  'billing.transactions.newer': 'Newer',
  'billing.transactions.older': 'Older',
  'billing.transactions.type.charge': 'Request charges',
  'billing.transactions.type.gift_card_redeem': 'Gift card credit',
  'billing.transactions.type.gift_direct_issue': 'Gift credit',
  'billing.transactions.type.gift_expiry_reclaim': 'Expired gift reclaimed',
  'billing.transactions.type.adjustment': 'Manual adjustment',
  'billing.transactions.type.topup': 'Top-up credit',
  'billing.transactions.type.external_topup': 'Offline top-up credit',
  'billing.pricing.title': 'Model pricing',
  'billing.pricing.unit': 'per 1M tokens · CNY',
  'billing.pricing.loading': 'Loading pricing…',
  'billing.pricing.error': 'Could not load pricing.',
  'billing.pricing.empty': 'No model pricing is ready to display yet.',
  'billing.pricing.column.vendor': 'Provider',
  'billing.pricing.column.model': 'Model',
  'billing.pricing.column.input': 'Input',
  'billing.pricing.column.output': 'Output',
  'billing.pricing.column.cache_read': 'Cache hit',
  'billing.pricing.column.cache_write': 'Cache write',
  'billing.pricing.column.effective_from': 'Effective from',

  // ── Billing statements (credit customers only) ─────────────────
  'billing.statements.title': 'Statements',
  'billing.statements.subtitle':
    'Accounts receivable per billing period. Usage is informational — the amount owed is the closing AR.',
  'billing.statements.error': 'Could not load statements.',
  'billing.statements.column.period': 'Period',
  'billing.statements.column.consumption': 'Usage (info)',
  'billing.statements.column.closing_ar': 'Closing AR',
  'billing.statements.column.due': 'Due date',
  'billing.statements.column.status': 'Status',
  'billing.statements.status.settled': 'Settled',
  'billing.statements.status.open': 'Open',
  'billing.statements.status.overdue': 'Overdue',
  'billing.statements.detail.title': 'Statement detail',
  'billing.statements.detail.period': 'Period',
  'billing.statements.detail.consumption': 'Usage (info)',
  'billing.statements.detail.opening_ar': 'Opening AR',
  'billing.statements.detail.payments': 'Payments applied',
  'billing.statements.detail.closing_ar': 'Closing AR',
  'billing.statements.detail.cny_reference': 'CNY reference',
  'billing.statements.detail.due': 'Due date',
  'billing.statements.detail.close': 'Close',
  'billing.statements.detail.consumption_hint':
    'Usage is the gross consumption for the period, for reference only; the amount owed is the closing AR.',

  // ── Billing topup page ─────────────────────────────────────────
  'topup.title': 'Top up',
  'topup.subtitle':
    'Fund your balance with WeChat Pay. The next step shows a QR code you can scan with your phone.',
  'topup.forbidden.title': 'Top-up restricted',
  'topup.forbidden.body': 'Only org owners and admins can add balance.',
  'topup.form.caption': 'Top-up order',
  'topup.forbidden.back': 'Back to billing',
  'topup.amount.label': 'Amount (CNY · whole yuan)',
  'topup.amount.hint': 'CNY · min {min} · max {max} · whole yuan only',
  'topup.usd.label': 'Balance value you will receive',
  'topup.usd.hint':
    'Converted at about {rate} ¥/$. The internal USD credit is locked when the order is created.',
  'topup.usd.hint_loading': 'Loading the current server quote.',
  'topup.usd.hint_unavailable':
    'Quote unavailable. The internal credit will be locked by the server when the order is created.',
  'topup.pay_type.legend': 'Payment method',
  'topup.pay_type.alipay': 'Alipay',
  'topup.pay_type.wxpay': 'WeChat Pay',
  'topup.error.whole_yuan': 'Enter a whole-yuan amount (integer, no decimals).',
  'topup.error.min': 'Minimum top-up is {amount}.',
  'topup.error.max': 'Maximum top-up is {amount}.',
  'topup.error.no_redirect': 'Payment channel did not return a redirect URL.',
  'topup.cancel': 'Cancel',
  'topup.submit': 'Continue to pay →',
  'topup.submitting': 'Creating…',
  'topup.fee_hint': 'Exchange rates refresh regularly, and Nxtpath covers the payment-channel fee.',
  'topup.qr.title': 'Scan to pay',
  'topup.qr.order_prefix': 'Order ',
  'topup.qr.image_alt': 'Payment QR code',
  'topup.qr.fallback_hint': 'Open WeChat on your phone and scan the target URL below.',
  'topup.qr.status.paid': 'Payment received. Redirecting…',
  'topup.qr.status.other': 'Order {status}',
  'topup.qr.status.pending': 'Waiting for payment…',
  'topup.qr.status.slow': 'Taking longer than expected…',
  'topup.qr.slow_notice':
    "We've stopped polling to save bandwidth. If you already paid in the app, your balance will update on its own — it's safe to close this.",
  'topup.qr.close': 'Close',

  // ── Settings page ──────────────────────────────────────────────
  'settings.title': 'Account & team',
  'settings.subtitle': 'Manage your account, teammates, and security settings.',
  // Shown when the members card is not: the page is then the personal
  // half only, and naming teammates would promise a section this reader
  // never gets.
  'settings.title_personal': 'Account',
  'settings.subtitle_personal': 'Manage your account and security settings.',
  'settings.retry': 'Retry',
  'settings.account.title': 'Account details',
  'settings.account.description': 'This section shows the information you use most often.',
  'settings.account.email': 'Email',
  'settings.account.account_id': 'Account id',
  'settings.account.org_id': 'Team id',
  'settings.account.email_verified': 'Email verified',
  'settings.account.status.verified': 'verified',
  'settings.account.status.unverified': 'unverified',
  'settings.technical.summary':
    'Raw IDs and other technical fields live here for troubleshooting when you need them.',
  'settings.members.title': 'Team members',
  'settings.members.subtitle':
    'Everyone who can access this team. Owners and admins can invite new members.',
  'settings.members.invite_button': 'Invite member',
  'settings.members.loading': 'Loading members…',
  'settings.members.error': 'Could not load members',
  'settings.members.empty': 'No members yet.',
  'settings.members.column.email': 'Email',
  'settings.members.column.role': 'Role',
  'settings.members.column.joined': 'Joined',
  'settings.members.column.actions': 'Actions',
  'settings.members.role.owner': 'Owner',
  'settings.members.role.admin': 'Admin',
  'settings.members.role.member': 'Member',
  'settings.members.role.finance': 'Finance',
  'settings.members.model_permissions': 'Model permissions',
  'settings.members.unrestricted': 'Unrestricted',
  'settings.model_permissions.title': 'Member model permissions',
  'settings.model_permissions.unit_title': 'Org unit model permissions',
  'settings.model_permissions.eyebrow': 'Model access',
  'settings.model_permissions.unit_eyebrow': 'Model access · org unit',
  'settings.model_permissions.subtitle': 'Configure the models {member} can use.',
  'settings.model_permissions.unit_subtitle': 'Configure the models {unit} passes down.',
  'settings.model_permissions.authorized_count': '{count} authorized',
  'settings.model_permissions.namespace_count': '{count} namespaces',
  'settings.model_permissions.mode.inherit': 'Inherit',
  'settings.model_permissions.mode.all': 'Allow all',
  'settings.model_permissions.mode.custom': 'Custom',
  'settings.model_permissions.mode_help.inherit': 'Follow the parent policy.',
  'settings.model_permissions.mode_help.all': 'Allow current and future catalog models.',
  'settings.model_permissions.mode_help.custom': 'Use only the selected models below.',
  'settings.model_permissions.inherited_from': 'From {source}',
  'settings.model_permissions.global_source': 'team defaults',
  'settings.model_permissions.inherit_active': 'Inheriting from {source}',
  'settings.model_permissions.inherit_body':
    'Switch to Custom to create a member-specific override.',
  'settings.model_permissions.unit_inherit_body':
    'Switch to Custom to create an override for this org unit.',
  'settings.model_permissions.all_models_active': 'All models are allowed',
  'settings.model_permissions.all_models_body': 'New catalog models are included automatically.',
  'settings.model_permissions.default_model': 'Default · {model}',
  'settings.model_permissions.search_label': 'Search model permissions',
  'settings.model_permissions.search_placeholder': 'Search namespace, vendor, or model...',
  'settings.model_permissions.no_search_results': 'No namespaces or models match this search.',
  'settings.model_permissions.select_namespace': 'Select all',
  'settings.model_permissions.clear_namespace': 'Clear all',
  'settings.model_permissions.legacy_title': 'Legacy grants',
  'settings.model_permissions.legacy_body':
    'These grants no longer match a catalog listing; saving keeps them unchanged.',
  'settings.model_permissions.retry': 'Retry',
  'settings.model_permissions.close': 'Close model permissions',
  'settings.model_permissions.models': 'models',
  'settings.model_permissions.default': 'Default',
  'settings.model_permissions.empty': 'No catalog models are available for authorization.',
  'settings.model_permissions.error': 'Could not load model permissions',
  'settings.model_permissions.cancel': 'Cancel',
  'settings.model_permissions.save': 'Save permissions',
  'settings.model_permissions.saving': 'Saving…',
  'settings.model_permissions.toast.saved': 'Model permissions saved',
  'settings.model_permissions.toast.unit_saved': 'Org unit model permissions saved',
  'settings.invitations.title': 'Pending invitations',
  'settings.invitations.loading': 'Loading…',
  'settings.invitations.error': 'Could not load invitations',
  'settings.invitations.empty': 'No pending invitations.',
  'settings.invitations.column.email': 'Email',
  'settings.invitations.column.role': 'Role',
  'settings.invitations.column.invited_by': 'Invited by',
  'settings.invitations.column.expires': 'Expires',
  'settings.invitations.column.actions': 'Actions',
  'settings.invitations.revoke': 'Revoke',
  'settings.invite_modal.title': 'Invite a member',
  'settings.invite_modal.description':
    'Invitees receive an email, and you can also copy the invite link to share it yourself.',
  'settings.invite_modal.email': 'Email',
  'settings.invite_modal.role': 'Role',
  'settings.invite_modal.role.member': 'Member',
  'settings.invite_modal.role.admin': 'Admin',
  'settings.invite_modal.role.finance': 'Finance',
  'settings.invite_modal.email_required': 'Email is required',
  'settings.invite_modal.cancel': 'Cancel',
  'settings.invite_modal.submit': 'Send invitation',
  'settings.invite_modal.submitting': 'Sending…',
  'settings.invite_modal.toast.sent': 'Invitation sent',
  'settings.invite_link.title': 'Invitation sent',
  'settings.invite_link.description':
    'The recipient will get an email with the link below. You can also copy it and share it manually.',
  'settings.invite_link.click_to_copy': 'Click to copy',
  'settings.invite_link.copied': 'Copied to clipboard',
  'settings.invite_link.copy_anywhere': 'Click anywhere on this block to copy',
  'settings.invite_link.done': 'Done',
  'settings.invite_link.aria_label': 'Copy invitation link to clipboard',
  'settings.security.title': 'Security',
  'settings.security.change_password': 'Change sign-in password',
  'settings.security.current_password': 'Current password',
  'settings.security.new_password': 'New password (10–128 chars)',
  'settings.security.confirm_password': 'Confirm new password',
  'settings.security.submit': 'Change password',
  'settings.security.submitting': 'Saving…',
  'settings.security.error.current_required': 'Current password is required',
  'settings.security.error.current_incorrect': 'Current password is incorrect',
  'settings.security.error.password_too_short': 'Password must be at least {min} characters',
  'settings.security.error.password_too_long': 'Password must be at most {max} characters',
  'settings.security.error.mismatch': 'Passwords do not match',
  'settings.security.error.network': 'Network error — try again',
  'settings.security.toast.success': 'Password changed',
  'settings.security.set_password.heading': 'Set a password',
  'settings.security.set_password.body':
    "This account signs in with emailed login codes and has no password yet. We'll email a 6-digit code to your address to set one.",
  'settings.security.set_password.action': 'Send code',
  'settings.security.set_password.action_cooldown': 'Send code ({seconds}s)',
  'settings.security.set_password.sending': 'Sending…',
  'settings.security.set_password.ack': 'Code sent — valid for {minutes} minutes.',
  'settings.security.set_password.code': 'Email code',
  'settings.security.set_password.submit': 'Set password',
  'settings.security.set_password.toast.success': 'Password set.',
  'settings.security.set_password.error.format': 'The code is 6 digits',
  'settings.security.set_password.error.invalid': 'Incorrect code',
  'settings.security.set_password.error.expired': 'Code expired — request a new one',
  'settings.security.set_password.error.attempts_exhausted':
    'Too many attempts — request a new code',
  'settings.security.set_password.error.send_rate_limited': 'Too many requests — try again later',

  // ── Promoter binding (tenant #192) ──────────────────────────────
  'promoter.redeem.bound': 'Referral code bound',
  'promoter.redeem.grant_issued': '{amount} referral credit, expires {date}',
  'promoter.redeem.grant_unconfigured': 'Referral credit pending operator configuration',
  'promoter.billing.bound': 'Referral code bound · {timestamp}',
  'promoter.grant.included': 'Includes {amount} referral credit',
  'promoter.grant.status_issued': 'Referral credit issued',
  'promoter.grant.expires_on': 'expires {date}',
  'promoter.grant.expiring_soon': 'expiring soon',

  // ── i18n switcher chrome ────────────────────────────────────────
  // The locale names themselves live in `LOCALE_NATIVE_LABELS` (each
  // option always renders in its own script); only the group label is
  // translated so screen readers announce it in the active locale.
  'i18n.switcher.aria_label': 'Language',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': 'Notifications',
  'toast.dismiss_aria': 'Dismiss notification',

  // ── Billing topup return ────────────────────────────────────────
  'billing.topup_return.page.title': 'Topup result',
  'billing.topup_return.page.order_label': 'Order ',
  'billing.topup_return.page.waiting_order_id': 'Waiting for order id…',
  'billing.topup_return.loading.aria': 'Loading order status…',
  'billing.topup_return.missing.title': 'Missing order id',
  'billing.topup_return.missing.body':
    "This page is normally reached from the payment provider's return link. If you landed here by mistake, go back to the billing overview.",
  'billing.topup_return.missing.cta': 'Return to billing',
  'billing.topup_return.pending.title': 'Payment in progress',
  'billing.topup_return.pending.body':
    'Payment is being processed. This page will update automatically once the provider confirms — no need to reload.',
  'billing.topup_return.slow.title': 'Payment takes longer than usual',
  'billing.topup_return.slow.body':
    "The provider hasn't confirmed your payment yet. If you already completed payment in the app, the balance will update within a few minutes — you can safely close this tab.",
  'billing.topup_return.slow.reference': 'Reference order id:',
  'billing.topup_return.slow.cta.back': 'Back to billing',
  'billing.topup_return.slow.cta.support': 'Contact support',
  'billing.topup_return.paid.title': 'Payment received',
  'billing.topup_return.paid.credited': 'Credited',
  'billing.topup_return.paid.charged': 'Charged',
  'billing.topup_return.paid.at_rate': 'at {rate} ¥/$',
  'billing.topup_return.paid.new_balance': 'New balance',
  'billing.topup_return.paid.balance_loading': 'Loading…',
  'billing.topup_return.paid.technical': 'Technical ledger details',
  'billing.topup_return.paid.usd_credit': 'USD credit',
  'billing.topup_return.paid.usd_ledger': 'USD ledger balance',
  'billing.topup_return.paid.redirect_hint':
    'Redirecting to the billing overview in a few seconds…',
  'billing.topup_return.paid.cta': 'Return to dashboard',
  'billing.topup_return.terminal.expired.title': 'Order expired',
  'billing.topup_return.terminal.expired.body':
    'This order expired before payment was completed. Start a new topup if you still want to add balance.',
  'billing.topup_return.terminal.failed.title': 'Payment failed',
  'billing.topup_return.terminal.failed.body':
    'The payment provider reported a failure for this order. No funds were charged — you can safely try again.',
  'billing.topup_return.terminal.refunded.title': 'Order refunded',
  'billing.topup_return.terminal.refunded.body':
    'This order was refunded. Start a new topup if you want to re-credit your balance.',
  'billing.topup_return.terminal.cta.retry': 'Try again',
  'billing.topup_return.terminal.cta.back': 'Back to billing',

  // ── Usage charts ────────────────────────────────────────────────
  'usage_chart.empty': 'No usage data for this range',
  'usage_chart.daily.aria':
    'Daily token usage — {days} days, {total} total tokens ({input} input, {output} output)',
  'usage_chart.daily.tooltip':
    '{date} — {total} tokens ({input} input + {output} output), {requests} requests',

  // ── Skeleton ────────────────────────────────────────────────────
  'skeleton.loading': 'Loading…',

  // ── Error banner ────────────────────────────────────────────────
  'error_banner.unexpected': 'Unexpected error',
  'error.network': 'Network error. Check the connection and try again.',
  'error.service': 'The service ran into a problem. Try again later.',
  'error.code.EMAIL_NOT_VERIFIED': 'Verify your email before signing in.',
  'error.code.INVALID_CREDENTIALS': 'Email or password is incorrect.',
  'error.code.ACCOUNT_SUSPENDED': 'This account is suspended.',
  'error.code.ACCOUNT_DELETED': 'This account has been closed.',
  'error.code.RATE_LIMITED': 'Too many attempts. Try again in a moment.',
  'error.code.EMAIL_ALREADY_REGISTERED': 'That email already has an account.',
  'error.code.EMAIL_FORMAT_INVALID': 'That email address is not valid.',
  'error.code.PASSWORD_LENGTH_INVALID': 'Password must be 10–128 characters.',
  'error.code.INVALID_VERIFICATION_TOKEN': 'This verification link is invalid.',
  'error.code.VERIFICATION_TOKEN_EXPIRED': 'This verification link has expired.',
  'error.code.VERIFICATION_TOKEN_CONSUMED': 'This verification link has already been used.',
  'error.code.AUTH_REQUIRED': 'Sign in to continue.',
  'error.code.FORBIDDEN': 'You do not have permission for this action.',
  'error.code.INVALID_PAYLOAD': 'The submitted data is not valid.',
  'error.code.CODE_INVALID': 'That code is not right.',
  'error.code.CODE_EXPIRED': 'That code has expired.',
  'error.code.CODE_RATE_LIMITED': 'Too many attempts with this code.',
  'error.code.INVITE_CODE_INVALID': 'This invite code is invalid.',
  'error.code.INVITE_CODE_QUOTA_EXHAUSTED': 'This invite code has no slots left today.',
  'error.code.INVITATION_EXPIRED': 'This invitation has expired.',
  'error.code.INVITATION_REVOKED': 'This invitation was revoked.',
  'error.code.INVITATION_ALREADY_ACCEPTED': 'This invitation has already been accepted.',
  'error.code.INVITATION_NOT_FOUND': 'This invitation could not be found.',
  'error.code.INVITATION_CONFLICT': 'This invitation is in a conflicting state.',
  'error.code.INSUFFICIENT_BALANCE': 'Balance is not enough for this action.',
  'error.code.TOPUP_AMOUNT_TOO_SMALL': 'That top-up amount is below the minimum.',
  'error.code.TOPUP_AMOUNT_TOO_LARGE': 'That top-up amount is above the maximum.',
  'error.code.TOPUP_PROVIDER_ERROR': 'The payment provider could not start this top-up.',
  'error.code.PAYMENT_ORDER_NOT_FOUND': 'That payment order could not be found.',
  'error.code.PAYMENT_PROVIDER_CONFIG_NOT_FOUND': 'Payment is not configured.',
  'error.code.GIFT_CARD_ALREADY_REDEEMED': 'This gift card has already been redeemed.',
  'error.code.GIFT_CARD_EXPIRED': 'This gift card has expired.',
  'error.code.GIFT_CARD_NOT_FOUND': 'This gift card could not be found.',
  'error.code.GIFT_CARD_REVOKED': 'This gift card has been revoked.',
  'error.code.PROMOTER_CODE_INVALID': 'This referral code is invalid or no longer active',
  'error.code.PROMOTER_ALREADY_BOUND': 'This account already has a referral code bound',
  'error.code.PROMOTER_BIND_WINDOW_CLOSED': 'The binding window has closed',
  'error.code.PROMOTER_SELF_BIND_FORBIDDEN': 'This account cannot bind a referral code',
  'error.code.PROMOTER_BIND_CONFLICT': 'This account is already attributed to a sales manager',
  'error.code.ORG_NOT_FOUND': 'That organization could not be found.',
  'error.code.ORG_ROLE_NOT_FOUND': 'That role could not be found.',
  'error.code.ORG_ROLE_NAME_TAKEN': 'That role name is already in use.',
  'error.code.ORG_ROLE_IN_USE': 'That role is still assigned and cannot be removed.',
  'error.code.KEY_NOT_FOUND': 'That key could not be found.',
  'error.code.API_KEY_NOT_FOUND': 'That API key could not be found.',
  'error.code.MODEL_ALIAS_NOT_FOUND': 'That model alias could not be found.',
  'error.code.MODEL_ALIAS_ALREADY_EXISTS': 'That model alias already exists.',
  'error.code.MODEL_MAPPING_STALE': 'Model mappings changed. Refresh and try again.',
  'error.code.MODEL_MAPPING_ROLLOUT_PENDING':
    'Model mappings are still rolling out. Try again shortly.',
  'error.code.MEMBER_MODEL_FORBIDDEN': 'This member is not allowed to use that model.',
  'error.code.MEMBER_MONTHLY_LIMIT_EXCEEDED': 'This member has reached the monthly limit.',
  'error.code.ATTACHMENT_NOT_FOUND': 'That attachment could not be found.',
  'error.code.SERVICE_UNAVAILABLE': 'The service is unavailable right now.',
  'error.code.INTERNAL': 'Something went wrong on the service side.',
  'error.code.NOT_IMPLEMENTED': 'This action is not available yet.',
  'error.code.TICKET_NOT_FOUND': 'This ticket does not exist or is not visible to this account',
  'error.code.TICKET_CLOSED': 'This ticket is closed.',
  'error.code.TICKET_LIMIT_EXCEEDED': 'The open-ticket limit has been reached.',
  'clipboard.error.denied': 'Clipboard access was denied.',
  'clipboard.error.unavailable': 'Clipboard is not available here.',
  'clipboard.error.rejected': 'Copy was rejected. Select the text and copy it yourself.',
  'clipboard.error.failed': 'Copy failed. Select the text and copy it yourself.',

  // ── Support tickets (/tickets, tenant #116) ─────────────────────
  'tickets.title': 'Support tickets',
  'tickets.subtitle': 'Open a ticket and follow the platform’s replies.',
  'tickets.create.caption': 'Submit a ticket',
  'tickets.create.type': 'Issue type',
  'tickets.create.type.billing': 'Top-up & billing',
  'tickets.create.type.usage': 'Usage & metering',
  'tickets.create.type.api': 'Keys & integration',
  'tickets.create.type.model': 'Models & quality',
  'tickets.create.type.other': 'Other',
  'tickets.create.email': 'Contact email',
  'tickets.create.email_hint': 'Replies also go to this account email.',
  'tickets.create.description': 'Description',
  'tickets.create.description_placeholder':
    'Describe the problem, what you expected, and any request ids.',
  'tickets.create.add_image': 'Add image',
  'tickets.create.remove_image': 'Remove {filename}',
  'tickets.create.attach_hint': 'Up to {max} images, 2MB each — PNG, JPG, WebP or GIF.',
  'tickets.create.submit': 'Submit ticket',
  'tickets.create.error.description_required': 'Description is required',
  'tickets.create.error.description_too_long': 'Keep the description under {max} characters',
  'tickets.create.error.too_many_images': 'Up to {max} images',
  'tickets.create.error.image_type': 'Only PNG, JPG, WebP or GIF images are supported',
  'tickets.create.error.image_size': 'Each image must be 2MB or smaller',
  'tickets.create.error.image_read': 'Could not read {filename}',
  'tickets.create.toast.submitted': 'Ticket submitted — the platform will follow up by email',
  'tickets.create.toast.attachments_unavailable':
    'Attachments are temporarily unavailable — submit a text-only ticket for now',
  'tickets.create.toast.limit':
    'Open-ticket limit reached — an organization can hold at most 20 unresolved tickets',
  'tickets.create.toast.rate_limited':
    'Submitting too often — each account can open 5 tickets per hour',
  'tickets.create.toast.failed': 'Could not submit the ticket',
  'tickets.status.all': 'All',
  'tickets.status.open': 'Open',
  'tickets.status.processing': 'Processing',
  'tickets.status.resolved': 'Resolved',
  'tickets.status.closed': 'Closed',
  'tickets.list.caption': 'My tickets',
  'tickets.list.filter_aria': 'Filter tickets by status',
  'tickets.list.loading': 'Loading tickets…',
  'tickets.list.error': 'Could not load tickets',
  'tickets.list.retry': 'Retry',
  'tickets.list.empty.title': 'No tickets yet',
  'tickets.list.empty.hint': 'Submitted tickets and their replies show up here.',
  'tickets.list.empty_filtered.title': 'No tickets in this status',
  'tickets.list.empty_filtered.hint': 'Switch back to all statuses to see every ticket.',
  'tickets.list.attachments': '{count} images',
  'tickets.list.pagination.summary': '{from}–{to} of {total}',
  'tickets.list.pagination.prev': 'Previous page',
  'tickets.list.pagination.next': 'Next page',
  'tickets.list.pagination.aria': 'Ticket pages',
  'tickets.error.not_found': 'This ticket does not exist or is not visible to this account',
  'tickets.replies.loading': 'Loading replies…',
  'tickets.replies.error': 'Could not load replies: {message}',
  'tickets.replies.empty': 'No replies yet',
  'tickets.replies.author.vendor': 'Nxtpath team',
  'tickets.replies.author.user': 'Me',
  'tickets.replies.placeholder': 'Add details or reply to the platform',
  'tickets.replies.closed_placeholder': 'This ticket is closed — replies are disabled',
  'tickets.replies.send': 'Reply',
  'tickets.replies.footer': 'Replies cannot be edited or deleted once sent.',
  'tickets.replies.toast.closed': 'This ticket is closed and cannot take new replies',
  'tickets.replies.toast.rate_limited': 'Replying too often — at most 30 replies per hour',
  'tickets.replies.toast.failed': 'Could not send the reply',
  'tickets.attachments.loading': 'Loading attachments…',
  'tickets.attachments.error': 'Failed to load attachments',
  'tickets.attachments.lightbox.title': 'View full image',
  'tickets.attachments.lightbox.close': 'Close',

  // ── Notifications (/notifications + header bell, tenant #116) ───
  'notifications.title': 'Notifications',
  'notifications.subtitle': 'Balance, budget, ticket and system events for this organization.',
  'notifications.empty.title': 'No notifications yet',
  'notifications.empty.hint': 'Ticket updates and platform events land here.',
  'notifications.unread_marker': 'New',
  'notifications.open_ticket_aria': 'Open the tickets page',
  'notifications.reply_prefix': 'Reply: ',
} as const;
